/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import {
	AssetType,
	deleteAsset,
	formatTemplateData,
	getTemplatesList,
} from 'api/services/AssetsService';
import {
	createAsset,
	deleteMediaService,
	generateFreeMediaService,
	generateMediaService,
	getCanvasAssetsService,
	getFaceReplaceCost,
	getMediaFeedService,
	getVoicesList,
	removeImageBackground,
	submitMediaFeedback,
	updateAsset,
	updateMediaProperties,
} from 'api/services/MediaService.ts';
import {
	createCanvas,
	deleteCanvas,
	getCanvas,
	getListCanvases,
	getListControlNetModels,
	getLoraCategories,
	getLoras,
	getModelCategories,
	getModels,
	getRandomPrompt,
	getStyleCategories,
	getStyles,
	updateCanvas,
} from 'api/services/PlaygroundService.ts';
import { toBase64 } from 'api/services/StudioService';
import { controlNetModelsConfig } from 'core/config/controlNetModelsConfig';
import { featureConfig } from 'core/config/featureConfig';
import { samplersConfig } from 'core/config/samplersConfig';
import { AmplitudeEvents } from 'core/constants/AmplitudeEvents';
import {
	DEFAULT_PROMPT_VALUE,
	DEFAULT_SELECTED_FLUX_MODEL,
	DEFAULT_SELECTED_FLUX_STYLE,
	DEFAULT_SELECTED_MODEL,
	DEFAULT_SELECTED_STYLE,
} from 'core/constants/constants';
import { ApiStage } from 'core/constants/enums/ApiStages.ts';
import { AspectRatio } from 'core/constants/enums/AspectRatioEnum.ts';
import { Qualities } from 'core/constants/enums/AvailableQualitiesEnum.ts';
import { CanvasImageSizeOptions } from 'core/constants/enums/CanvasImageSizeOptions.ts';
import { CanvasLoraLocalCategories } from 'core/constants/enums/CanvasLoraLocalCategories.ts';
import { CanvasShareMode } from 'core/constants/enums/CanvasShareMode.ts';
import { ControlNetModelsType } from 'core/constants/enums/ControlNetModelsType.ts';
import { GenerationModelTypes } from 'core/constants/enums/GenerationModelTypes.ts';
import { GenerationStyleTabs } from 'core/constants/enums/GenerationStyleTabs.ts';
import { GenerationStyleTypes } from 'core/constants/enums/GenerationStyleTypes.ts';
import { GenerationType } from 'core/constants/enums/GenerationType.ts';
import { MediaGenerationStatus } from 'core/constants/enums/MediaGenerationStatus.ts';
import { PriorityView } from 'core/constants/enums/PriorityView.ts';
import { ProModeTabs } from 'core/constants/enums/ProModeTabs.ts';
import { RendernetErrors } from 'core/constants/enums/RendernetErrors.ts';
import { Samplers } from 'core/constants/enums/SamplersEnum.ts';
import { StudioMediaTypeEnums } from 'core/constants/enums/StudioMediaTypeEnums.ts';
import { StudioSidebarViews } from 'core/constants/enums/StudioSidebarViews.ts';
import { TemplateType } from 'core/constants/enums/TemplateType.ts';
import { ToastType } from 'core/constants/enums/ToastTypeEnum.ts';
import { UpscaleResolution } from 'core/constants/enums/UpscaleResolution.ts';
import { PostHogEvents } from 'core/constants/PostHogEvents';
import { sendToast } from 'core/layouts/components/toast/ToastUtils';
import { STUDIO } from 'core/navigation/Routes';
import {
	addCanvasFeedItems,
	removeCanvasFeedItems,
	updateCanvasFeedItems,
} from 'core/store/reducers/canvasReducer/canvasDataMappingUtils';
import { getMusicVideoAsync } from 'core/store/reducers/intermediateMediaReducer/IntermediateMediaSlice';
import { captureAmplitudeEvent } from 'core/utils/AmplitudeUtils.ts';
import { findOptimalPositionAlt } from 'core/utils/canvasPositionFinderUtil';
import { deleteCanvasData } from 'core/utils/LocalStorageUtil';
import { LoggerUtil } from 'core/utils/loggerUtil.ts';
import { capturePosthogEvent } from 'core/utils/PosthogUtil.ts';
import {
	handleSomethingWentWrongErrorWithToast,
	noNullCheckForObjectValues,
	saveUserCanvasPreferenceInLocalStorage,
	toggleModelSelection,
} from 'core/utils/utils';

// const canvasPositionFinder = new CanvasPositionFinder();
const selectedModelsDefaultInput = [DEFAULT_SELECTED_MODEL];
const selectedStylesDefaultInput = DEFAULT_SELECTED_STYLE;
const selectedDefaultFluxModels = DEFAULT_SELECTED_FLUX_MODEL;
const selectedDefaultFluxStyles = DEFAULT_SELECTED_FLUX_STYLE;
const selectedDefaultLightningModels = [];

const promptDefaultInput = DEFAULT_PROMPT_VALUE;
const negativePromptDefaultInput =
	'worst, low quality, extra limbs, bad anatomy, bad hands, nsfw, nudity, exposed genitals, breasts';
const samplerDefaultInput = samplersConfig[Samplers.DPM_2M_KARRAS].name;
const samplingStepsDefaultInput = 25;
const APGuidanceDefaultInput = 6;
export const batchSizeDefaultInput = 1;
const seedDefaultInput = '-1';
const randomiseSeedDefaultInput = true;
const aspectRatioDefaultInput = CanvasImageSizeOptions.PORTRAIT;
const qualityDefaultInput = Qualities.PLUS;

const controlNetDefaultEnabled = false;
const controlNetDefaultImage = null;
const controlNetNonSDXLDefaultType = controlNetModelsConfig[ControlNetModelsType.SD15];
const controlNetSDXLDefaultType = controlNetModelsConfig[ControlNetModelsType.SDXL];
const controlNetFluxDefaultType = controlNetModelsConfig[ControlNetModelsType.Flux];

const controlNetControlDefaultMode = 1;
const controlNetResizeDefaultMode = 1;

const trueTouchDefaultImage = null;
const trueTouchDefaultScaleFactor = 2;
const trueTouchDefaultRealismEffect = 0.2;
const trueTouchDefaultCanvasImageAdded = false;

const faceSwapDefaultImageAssetData = null;
const faceSwapDefaultImageAdded = false;

const faceSwapDefaultVideoAssetData = null;

const facelockDefaultEnabled = false;
const facelockDefaultImage = null;

const faceSwapDefaultRenderingPreference = null;
const faceSwapDefaultFrames = 0;

const lipSyncDefaultVideoAssetData = null;
const lipSyncDefaultVoiceData = null;
const lipSyncDefaultScript = '';
const lipSyncDefaultImageAssetData = null;
const lipSyncDefaultVoicesList = [];
const lipSyncDefaultVoicesApiStage = ApiStage.IDLE;
const lipSyncDefaultAudioAssetData = null;
const lipSyncDefaultIsFreeVideoUsed = false;

const upscaleDefaultImage = null;
const upscaleDefaultOutputResolution = UpscaleResolution.RESOLUTION_4K;
const upscaleDefaultCanvasImageAdded = false;

const videoAnyoneDefaultCanvasImageAdded = false;
const videoAnyoneDefaultImage = null;
const videoAnyoneDefaultPrompt = '';

const videoFaceSwapDefaultImage = null;
const videoFaceSwapDefaultVideo = null;

const intensityDefaultCount = 7;

const initialState = {
	canvasId: '',
	canvasPreferences: {},
	sidebarView: StudioSidebarViews.NONE,

	models: [],
	fluxModels: [],
	lightningModels: [],
	getModelsAPIStage: ApiStage.LOADING,
	selectedModels: selectedModelsDefaultInput,
	selectedStyles: selectedStylesDefaultInput,
	selectedFluxModels: selectedDefaultFluxModels,
	selectedLightningModels: selectedDefaultLightningModels,

	getCanvasFeedAPIStage: ApiStage.IDLE,
	canvasFeedMap: {},
	canvasFeedDataMap: {},
	canvasMediaUpdatePendingList: [],
	canvasPendingUpdateAssetsList: [],

	promptInput: {},
	negativePromptInput: negativePromptDefaultInput,
	samplerInput: samplerDefaultInput,
	samplingStepsInput: samplingStepsDefaultInput,
	AIGuidanceInput: APGuidanceDefaultInput,
	batchSizeInput: batchSizeDefaultInput,
	seedInput: seedDefaultInput,
	randomiseSeedInput: randomiseSeedDefaultInput,
	aspectRatioInput: aspectRatioDefaultInput,

	quality: qualityDefaultInput,
	priority: PriorityView.QUALITY,

	loras: [],
	getLorasAPIStage: ApiStage.LOADING,
	selectedLoras: [],

	controlNetEnabled: controlNetDefaultEnabled,
	controlNetImage: controlNetDefaultImage,
	controlNetNonSDXLType: controlNetNonSDXLDefaultType,
	controlNetSDXLType: controlNetSDXLDefaultType,
	controlNetFluxType: controlNetFluxDefaultType,
	controlNetControlMode: controlNetControlDefaultMode,
	controlNetResizeMode: controlNetResizeDefaultMode,
	controlNetModelsCanvas: null,
	canvasControlNetModelsApiStage: ApiStage.IDLE,

	trueTouchImage: trueTouchDefaultImage,
	isTrueTouchImageUploading: false,
	trueTouchScaleFactor: trueTouchDefaultScaleFactor,
	trueTouchRealismEffect: trueTouchDefaultRealismEffect,
	trueTouchCanvasImageAdded: trueTouchDefaultCanvasImageAdded,

	facelockEnabled: facelockDefaultEnabled,
	facelockImage: facelockDefaultImage,

	toggleSaveGenerationalParametersInLocalStorage: true,
	generateCanvasImageAPIStage: ApiStage.IDLE,

	openTooManyGenerationsModal: false,
	openTooManyVideoGenerationsModal: false,

	moveStageToLatestGeneration: false,
	scaleCanvasStage: 1,
	canvasScaled: false,
	fitToScreen: false,
	isCanvasDraggable: false,

	randomisePromptAPIStage: ApiStage.IDLE,

	canvases: [],
	listCanvasApiStage: ApiStage.IDLE,
	refetchListCanvas: true,
	getCanvasAPIStage: ApiStage.IDLE,
	canvasProperty: {},
	refetchCanvasData: {},
	canvasViewportCenterMap: {},

	createdCanvasId: null,
	createCanvasApiStage: ApiStage.IDLE,

	updateCanvasAPIStage: ApiStage.IDLE,

	deleteCanvasApiStage: ApiStage.IDLE,
	deleteImageApiStage: ApiStage.IDLE,

	modelCategories: [],
	getModelCategoriesAPIStage: ApiStage.IDLE,

	loraCategories: [],
	getLoraCategoriesAPIStage: ApiStage.IDLE,

	styleCategories: [],
	getStyleCategoriesApiStage: ApiStage.IDLE,

	styles: [],
	fluxStyles: [],
	getStylesApiStage: ApiStage.IDLE,
	selectedFluxStyles: selectedDefaultFluxStyles,

	currentProModeTab: ProModeTabs.MODELS,

	currentGenerationStyleTab: GenerationStyleTabs.MAGIC_MODE,

	isImageSelected: false,
	isMultipleImagesSelected: false,
	canvasSelectedMedia: null,

	faceSwapImageAssetData: faceSwapDefaultImageAssetData,
	faceSwapImageUploading: false,
	faceSwapImageAdded: faceSwapDefaultImageAdded,
	faceSwapVideoAssetData: faceSwapDefaultVideoAssetData,
	faceSwapRenderingPreference: faceSwapDefaultRenderingPreference,
	faceSwapFrames: faceSwapDefaultFrames,

	lipSyncVideoAssetData: lipSyncDefaultVideoAssetData,
	lipSyncVoiceData: lipSyncDefaultVoiceData,
	lipSyncScript: lipSyncDefaultScript,
	lipSyncImageAssetData: lipSyncDefaultImageAssetData,
	lipSyncVoicesList: lipSyncDefaultVoicesList,
	lipSyncVoicesApiStage: lipSyncDefaultVoicesApiStage,
	lipSyncAudioAssetData: lipSyncDefaultAudioAssetData,
	lipSyncIsFreeVideoUsed: lipSyncDefaultIsFreeVideoUsed,

	upscaleInputImage: upscaleDefaultImage,
	upscaleOutputResolution: upscaleDefaultOutputResolution,
	upscaleCanvasImageAdded: upscaleDefaultCanvasImageAdded,

	videoAnyoneInputImage: videoAnyoneDefaultImage,
	videoAnyoneCanvasImageAdded: videoAnyoneDefaultCanvasImageAdded,
	videoAnyoneInputImageId: '',
	videoAnyonePrompt: videoAnyoneDefaultPrompt,
	videoAnyoneDuration: null,
	videoAnyoneEndFrameImage: { id: null, url: null },
	videoAnyoneModelName: null,
	videoAnyoneModelProvider: null,

	videoFaceSwapInputImage: videoFaceSwapDefaultImage,
	videoFaceSwapInputVideo: videoFaceSwapDefaultVideo,

	canvasMediaFeedbackModalOpen: false,
	submitCanvasMediaFeedbackApiStage: ApiStage.IDLE,

	canvasAssetsMap: {},
	getCanvasAssetsApiStage: ApiStage.IDLE,
	canvasNewAssetIdMap: {},

	isCanvasUpdating: false,

	isCanvasInvalidError: false,
	isCanvasNotPublicError: false,

	selectedCanvasMediaThumbnail: null,

	faceReplaceInputImage: null,
	faceReplaceInputImageId: '',
	faceReplaceTargetImages: [],
	faceReplaceTargetImageIndex: -1,
	faceReplaceCostAPIStage: ApiStage.IDLE,
	faceReplaceCost: 0,
	canvasAppBarMediaType: StudioMediaTypeEnums.IMAGE,
	mediaToolsSidebarView: null,

	musicVideoAudioAssetData: null,
	musicVideoTheme: '',
	musicVideoCharacter: null,
	musicVideoAspectRatio: AspectRatio.WIDE,
	musicVideoTemplatesData: null,
	musicVideoTemplatesApiStage: ApiStage.IDLE,

	// clothing change
	clothingInputImage: { id: null, url: null },
	clothingTargetImage: { id: null, url: null },
	clothingTypeValue: null,

	// Product Photo
	productPhotoImage: { id: null, url: null, displayOrder: null },
	productPhotoWithoutBackgroundImageApiStage: ApiStage.IDLE,
	productPhotoWithoutBackgroundImage: { id: null, url: null },
	productPhotoBackgroundImage: { id: null, url: null },
	productPhotoObjectLocationImage: { id: null, url: null },
	productPhotoCompositeImage: { id: null, url: null },
	productPhotoMode: 'steady',
	productPhotoReflection: false,
	productPhotoObjectTransformValues: { rotation: null, flipX: false, flipY: false },
	productPhotoImageToVideoGuideToggle: { modalOpen: false, doNotShowValue: false },

	// Image Reference
	imageReferenceStyle: { id: null, url: null },
	intensityCountInput: intensityDefaultCount,
};

export const canvasInitialState = initialState;

// Models Service
export const getModelsAsync = createAsyncThunk('canvas/getModels', async (type, { dispatch }) =>
	getModels(type, { dispatch }),
);
export const getModelCategoriesAsync = createAsyncThunk(
	'canvas/getModelCategories',
	async (_, { dispatch }) => getModelCategories({ dispatch }),
);
export const getControlNetModelsAsync = createAsyncThunk(
	'canvas/getControlNetModels',
	async (_, { dispatch }) => getListControlNetModels({ dispatch }),
);

// Style Service
export const getStylesAsync = createAsyncThunk('canvas/getStyles', async (type, { dispatch }) =>
	getStyles(type, { dispatch }),
);
export const getStylesCategoryAsync = createAsyncThunk(
	'canvas/getStylesCategory',
	async (_, { dispatch }) => getStyleCategories({ dispatch }),
);

// Loras Service
export const getLorasAsync = createAsyncThunk('canvas/getLoras', async (_, { dispatch }) =>
	getLoras({ dispatch }),
);
export const getLoraCategoriesAsync = createAsyncThunk(
	'canvas/getLoraCategories',
	async (_, { dispatch }) => getLoraCategories({ dispatch }),
);

export const getAudioAssetsTemplatesAsync = createAsyncThunk(
	'canvas/getAudioAssetsTemplates',
	async (_, { dispatch }) => {
		return getTemplatesList(TemplateType.MUSIC_VIDEO_SONGS, { dispatch });
	},
);

export const uploadCanvasControlNetImageFileAsync = createAsyncThunk(
	'canvas/uploadControlNetImageFile',
	async (file) => toBase64(file),
);
export const uploadCanvasControlNetImageLinkAsync = createAsyncThunk(
	'canvas/uploadControlNetImageLink',
	async (link) => Promise.resolve(link),
);

export const uploadCanvasTrueTouchImageFileAsync = createAsyncThunk(
	'canvas/uploadTrueTouchImageFile',
	async (file) => toBase64(file),
);
export const uploadCanvasTrueTouchImageLinkAsync = createAsyncThunk(
	'canvas/uploadTrueTouchImageLink',
	async (link) => Promise.resolve(link),
);

export const uploadCanvasFaceLockImageFileAsync = createAsyncThunk(
	'canvas/uploadFaceLockImageFile',
	async (file) => toBase64(file),
);
export const uploadCanvasFaceLockImageLinkAsync = createAsyncThunk(
	'canvas/uploadFaceLockImageLink',
	async (link) => Promise.resolve(link),
);

export const uploadCanvasFaceSwapImageLinkAsync = createAsyncThunk(
	'canvas/uploadFaceSwapImageLink',
	async (link) => Promise.resolve(link),
);

export const getCanvasFeedAsync = createAsyncThunk(
	'canvas/getCanvasFeedAsync',
	async (newPage, { getState }) => {
		const state = getState();
		// eslint-disable-next-line @typescript-eslint/no-unused-vars,no-unused-vars
		const { canvasId } = state.canvas;
		return getMediaFeedService({
			canvasId,
			playground: 'canvas',
		});
	},
);

export const getCanvasAssetsAsync = createAsyncThunk(
	'canvas/getCanvasAssetsAsync',
	async (arg, { getState }) => {
		const state = getState();
		// eslint-disable-next-line @typescript-eslint/no-unused-vars,no-unused-vars
		const { canvasId } = state.canvas;
		return getCanvasAssetsService(canvasId);
	},
);

export const listCanvases = createAsyncThunk(
	'canvas/listCanvases',
	async (_, { dispatch, getState }) =>
		getListCanvases(undefined, undefined, dispatch, getState().canvas.refetchListCanvas),
);

export const createCanvasAsync = createAsyncThunk('canvas/createCanvas', async (_, { dispatch }) =>
	createCanvas(dispatch),
);

export const runGenerateCanvasMediaAsync = createAsyncThunk(
	'canvas/runGenerateCanvasMedia',
	async (data, { getState }) => {
		const { generateParams } = data;
		const {
			canvasId,
			promptInput: prompt,
			models,
			styles,
			fluxModels,
			fluxStyles,
			lightningModels,
			currentGenerationStyleTab,
			canvasFeedDataMap,
			canvasViewportCenterMap,
			selectedCanvasMedia,
			imageReferenceStyle,
		} = getState().canvas;
		if (generateParams.selectedStyles || generateParams.selectedModels) {
			saveUserCanvasPreferenceInLocalStorage({
				canvasId,
				...generateParams,
				promptInput: prompt?.[canvasId] || promptDefaultInput,
				negativePromptInput: generateParams?.negativePrompt,
				aspectRatioInput: generateParams?.aspectRatio,
				characterId: generateParams?.character?.id,
			});
		}
		// const canvasItemsPositionData = Object.values(canvasFeedDataMap[canvasId]?.media ?? {}).reduce(
		// 	(acc, value) => ({
		// 		...acc,
		// 		[value.id]: {
		// 			x: value.x,
		// 			y: value.y,
		// 			width: value.width,
		// 			height: value.height,
		// 		},
		// 	}),
		// 	{},
		// );

		const canvasItemsPositionData = Object.values(canvasFeedDataMap[canvasId]?.media ?? {}).map(
			(canvasMedia) => [canvasMedia.x, canvasMedia.y, canvasMedia.width, canvasMedia.height],
		);
		let generateResponse;
		if (generateParams.isFreeGeneration) {
			const freeGenerateParams = {
				...generateParams,
				canvasId,
			};
			LoggerUtil.logInfo('Generating free media on canvas with params:', freeGenerateParams);
			generateResponse = await generateFreeMediaService(freeGenerateParams);
		} else {
			const isFluxGeneration =
				currentGenerationStyleTab === StudioSidebarViews.FLUX &&
				!featureConfig.isFluxControlNetEnabled;
			const finalGenerateParams = {
				canvasId,
				...generateParams,
				models: { ...models, ...fluxModels, ...lightningModels },
				styles: [...styles, ...fluxStyles],
				controlNetOptions: isFluxGeneration ? null : generateParams.controlNetOptions,
				prompt: generateParams.prompt?.[canvasId] ?? promptDefaultInput,
				imageReferenceOptions:
					imageReferenceStyle.url != null ? generateParams.imageReferenceOptions : null,
			};
			LoggerUtil.logInfo('Generating media on canvas with params:', finalGenerateParams);
			generateResponse = await generateMediaService(finalGenerateParams);
		}
		const { formattedDerivedMedia } = generateResponse;
		const maxWidth = Object.values(formattedDerivedMedia)?.reduce(
			(max, media) => Math.max(max, media.width),
			0,
		);
		const maxHeight = Object.values(formattedDerivedMedia)?.reduce(
			(max, media) => Math.max(max, media.height),
			0,
		);

		const position = findOptimalPositionAlt(
			canvasItemsPositionData,
			[(maxWidth + 10) * Object.keys(formattedDerivedMedia).length, maxHeight],
			10,
			{
				x: canvasViewportCenterMap?.[canvasId].x ?? 0,
				y: canvasViewportCenterMap?.[canvasId]?.y ?? 0,
			},
			selectedCanvasMedia
				? [
						selectedCanvasMedia.x,
						selectedCanvasMedia.y,
						selectedCanvasMedia.width,
						selectedCanvasMedia.height,
					]
				: null,
		);
		const positionData = { x: position[0], y: position[1] };
		let currentWidth = 0;
		Object.keys(formattedDerivedMedia).forEach((mediaId) => {
			formattedDerivedMedia[mediaId] = {
				...formattedDerivedMedia[mediaId],
				x: currentWidth + positionData.x,
				y: positionData.y,
			};
			currentWidth += formattedDerivedMedia[mediaId].width + 10;
		});
		return { formattedDerivedMedia };
	},
);

export const randomiseCanvasPromptAsync = createAsyncThunk(
	'canvas/randomisePromptAsync',
	async () => getRandomPrompt(),
);

export const deleteCanvasAsync = createAsyncThunk(
	'canvas/deleteCanvas',
	async (canvasId, { dispatch }) => deleteCanvas(canvasId, dispatch),
);

export const updateMediaPropertiesAsync = createAsyncThunk(
	'canvas/updateMediaProperties',
	async (mediaProperties, { getState }) => {
		const state = getState();
		const { canvasId } = state.canvas;
		return updateMediaProperties(canvasId, mediaProperties);
	},
);

export const updateCanvasAssetsAsync = createAsyncThunk(
	'canvas/updateCanvasAssetsAsync',
	async (assetList, { getState }) => {
		const state = getState();
		const { canvasId } = state.canvas;
		const newAssets = [];
		const updatedAssets = [];
		assetList.forEach((asset) => {
			if (asset.isNew) newAssets.push(asset);
			else updatedAssets.push(asset);
		});
		let assetIdMap = {};
		let assetDataMap = {};
		if (newAssets.length) {
			const dataMap = await createAsset(canvasId, newAssets);
			assetIdMap = { ...assetIdMap, ...dataMap?.data?.assetIdMap };
			assetDataMap = { ...assetDataMap, ...dataMap?.data?.assetDataMap };
		}
		if (updatedAssets.length) {
			const dataMap = await updateAsset(canvasId, updatedAssets);
			assetIdMap = { ...assetIdMap, ...dataMap?.data?.assetIdMap };
			assetDataMap = { ...assetDataMap, ...dataMap?.data?.assetDataMap };
		}
		return { assetIdMap, assetDataMap };
	},
);
export const getCanvasAsync = createAsyncThunk(
	'canvas/getCanvas',
	async ({ canvasId }, { dispatch, getState }) =>
		getCanvas(canvasId, dispatch, getState().canvas.refetchCanvasData[canvasId] ?? false),
);

export const updateCanvasAsync = createAsyncThunk(
	'canvas/updateCanvasProperties',
	async ({ canvasId, canvasProperty, isPublicMode }, { dispatch }) => {
		if (canvasId !== '' && !isPublicMode) {
			return updateCanvas(canvasId, canvasProperty, dispatch);
		}
		return null;
	},
);

// export const deleteImageAsync = createAsyncThunk(
// 	'canvas/deleteImage',
// 	async (imageId, { dispatch, getState }) => {
// 		const state = getState();
// 		const { canvasFeedMap } = state.canvas;
// 		const canvasFeedMapCopy = { ...canvasFeedMap };
//
// 		delete canvasFeedMapCopy[imageId];
//
// 		// NOTE:
// 		// DISABLING no-use-before-define FOR LINE BELOW BECAUSE
// 		// NECESSARY TO ACCESS THIS REDUCER AT THIS STAGE TO REMOVE
// 		// DELETED IMAGE FROM FEED STATE TO PREVENT THE LAG FROM API CALL
// 		// eslint-disable-next-line no-use-before-define
// 		dispatch(setCanvasFeedMap(canvasFeedMapCopy));
// 		return deleteMediaService(imageId);
// 	},
// );

export const deleteMediaAsync = createAsyncThunk(
	'canvas/deleteMedia',
	async (mediaIds, { dispatch, getState }) => {
		const state = getState();
		const { canvasFeedDataMap, canvasId } = state.canvas;

		// NOTE:
		// DISABLING no-use-before-define FOR LINE BELOW BECAUSE
		// NECESSARY TO ACCESS THIS REDUCER AT THIS STAGE TO REMOVE
		// DELETED IMAGE FROM FEED STATE TO PREVENT THE LAG FROM API CALL
		dispatch(
			// eslint-disable-next-line no-use-before-define
			setCanvasFeedMap(removeCanvasFeedItems(canvasFeedDataMap, canvasId, 'media', mediaIds)),
		);
		return deleteMediaService(mediaIds);
		// return deleteMediaService(imageId, { dispatch });
	},
);

export const deleteAssetAsync = createAsyncThunk(
	'canvas/deleteAsset',
	async (assetIdMap, { dispatch, getState }) => {
		const state = getState();
		const { canvasFeedDataMap, canvasId } = state.canvas;
		const assetIds = Object.keys(assetIdMap);
		const deletedAssetIds = assetIds.map((id) => [id, assetIdMap[id]]).flat(2);

		dispatch(
			// eslint-disable-next-line no-use-before-define
			setCanvasFeedMap(
				removeCanvasFeedItems(canvasFeedDataMap, canvasId, 'assets', deletedAssetIds),
			),
		);
		return deleteAsset(assetIds, 'text');
	},
);

export const getLipSyncVoicesListAsync = createAsyncThunk(
	'canvas/getLipSyncVoicesList',
	async (_, { dispatch }) => getVoicesList({ dispatch }),
);

export const submitCanvasMediaFeedbackAsync = createAsyncThunk(
	'canvas/submitFeed',
	async (data, { dispatch }) =>
		submitMediaFeedback(data.mediaId, data.comment, data.rating, { dispatch }),
);

export const getCanvasFaceReplaceCostAsync = createAsyncThunk(
	'canvas/getFaceReplaceCost',
	async (_, { dispatch }) => getFaceReplaceCost({ dispatch }),
);

export const productPhotoCanvasRemoveImageBackgroundAsync = createAsyncThunk(
	'canvas/productPhotoRemoveBackgroundImage',
	async (inputImageId, { dispatch }) => removeImageBackground(inputImageId, { dispatch }),
);

export const canvasSlice = createSlice({
	name: 'canvas',
	initialState,
	reducers: {
		setCanvasId: (state, action) => {
			state.canvasId = action.payload;
		},
		setCanvasProperty: (state, action) => {
			state.canvasProperty = action.payload;
		},
		setCanvasSidebarView: (state, action) => {
			state.sidebarView = action.payload;
		},
		setCanvasSettings: (state, action) => {
			const { settings, fromLocalStorage } = action.payload;

			state.samplerInput = settings?.sampler ?? samplerDefaultInput;
			state.samplingStepsInput = settings?.steps ?? samplingStepsDefaultInput;
			state.AIGuidanceInput = settings?.guidance ?? APGuidanceDefaultInput;

			const { canvasId } = state;
			state.promptInput = {
				...state.promptInput,
				[canvasId]: settings?.prompt ?? promptDefaultInput,
			};
			state.negativePromptInput = settings?.negativePrompt ?? negativePromptDefaultInput;

			state.batchSizeInput = settings?.batchSize ?? 1;

			state.seedInput = settings?.seed ?? seedDefaultInput;
			state.randomiseSeedInput = settings?.randomiseSeed ?? randomiseSeedDefaultInput;

			state.aspectRatioInput = settings?.aspectRatioType ?? aspectRatioDefaultInput;

			const resetControlNetSettingsToDefault = () => {
				state.controlNetImage = controlNetDefaultImage;
				state.controlNetEnabled = controlNetDefaultEnabled;
				state.controlNetNonSDXLType = controlNetNonSDXLDefaultType;
				state.controlNetSDXLType = controlNetSDXLDefaultType;
				state.controlNetFluxType = controlNetFluxDefaultType;
				state.controlNetControlMode = controlNetControlDefaultMode;
				state.controlNetResizeMode = controlNetResizeDefaultMode;
			};
			const resetFaceLockSettingsToDefault = () => {
				state.facelockEnabled = facelockDefaultEnabled;
				state.facelockImage = facelockDefaultImage;
			};

			if (settings?.attributes) {
				const { controlNet, facelock, loras } = settings.attributes;
				if (controlNet) {
					let enableControlNet;
					if (fromLocalStorage) {
						enableControlNet = controlNet.enabled;
					} else {
						enableControlNet = noNullCheckForObjectValues(controlNet);
					}
					state.controlNetEnabled = enableControlNet;

					if (enableControlNet || fromLocalStorage) {
						state.controlNetControlMode = controlNet?.controlMode
							? controlNet?.controlMode
							: controlNetControlDefaultMode;
						state.controlNetResizeMode = controlNet?.resizeMode
							? controlNet?.resizeMode
							: controlNetResizeDefaultMode;

						state.controlNetNonSDXLType =
							controlNet?.nonsdxl && state.controlNetModelsCanvas
								? Object.values(state.controlNetModelsCanvas).find(
										(config) => config?.id === controlNet?.nonsdxl?.id,
									)
								: controlNetNonSDXLDefaultType;
						state.controlNetSDXLType =
							controlNet?.sdxl && state.controlNetModelsCanvas
								? Object.values(state.controlNetModelsCanvas).find(
										(config) => config?.id === controlNet?.sdxl?.id,
									)
								: controlNetSDXLDefaultType;
						// TODO: FLUX ControlNet Changes
						state.controlNetFluxType =
							controlNet?.flux && state.controlNetModelsStudio
								? Object.values(state.controlNetModelsStudio).find(
										(config) => config.id === controlNet?.flux?.id,
									)
								: controlNetFluxDefaultType;
					} else {
						resetControlNetSettingsToDefault();
					}
				} else {
					resetControlNetSettingsToDefault();
				}
				if (facelock) {
					let enableFacelock;
					if (fromLocalStorage) {
						enableFacelock = facelock.enabled;
					} else {
						enableFacelock = noNullCheckForObjectValues(facelock);
					}
					state.facelockEnabled = enableFacelock;
				} else {
					resetFaceLockSettingsToDefault();
				}
				if (loras) {
					state.selectedLoras = Array.isArray(loras) ? loras : [];
				} else {
					state.selectedLoras = [];
				}
			} else {
				resetControlNetSettingsToDefault();
				resetFaceLockSettingsToDefault();
			}
		},
		resetCanvasSettings: (state) => {
			state.selectedModels = selectedModelsDefaultInput;
			state.selectedStyles = selectedStylesDefaultInput;
			state.selectedLightningModels = selectedDefaultLightningModels;
			state.selectedFluxModels = selectedDefaultFluxModels;
			state.selectedFluxStyles = selectedDefaultFluxStyles;

			state.samplerInput = samplerDefaultInput;
			state.samplingStepsInput = samplingStepsDefaultInput;
			state.AIGuidanceInput = APGuidanceDefaultInput;

			state.negativePromptInput = negativePromptDefaultInput;

			state.batchSizeInput = batchSizeDefaultInput;

			state.seedInput = seedDefaultInput;
			state.randomiseSeedInput = randomiseSeedDefaultInput;

			state.aspectRatioInput = aspectRatioDefaultInput;

			state.selectedCanvasMediaThumbnail = null;
			state.selectedCanvasMedia = null;

			state.controlNetImage = controlNetDefaultImage;
			state.controlNetEnabled = controlNetDefaultEnabled;
			state.controlNetNonSDXLType = controlNetNonSDXLDefaultType;
			state.controlNetSDXLType = controlNetSDXLDefaultType;
			state.controlNetFluxType = controlNetFluxDefaultType;
			state.controlNetControlMode = controlNetControlDefaultMode;
			state.controlNetResizeMode = controlNetResizeDefaultMode;

			state.trueTouchImage = trueTouchDefaultImage;
			state.isTrueTouchImageUploading = false;
			state.trueTouchScaleFactor = trueTouchDefaultScaleFactor;
			state.trueTouchRealismEffect = trueTouchDefaultRealismEffect;
			state.trueTouchCanvasImageAdded = trueTouchDefaultCanvasImageAdded;

			state.facelockEnabled = facelockDefaultEnabled;
			state.facelockImage = facelockDefaultImage;

			state.faceSwapVideoAssetData = faceSwapDefaultVideoAssetData;
			state.faceSwapImageAssetData = faceSwapDefaultImageAssetData;
			state.faceSwapRenderingPreference = faceSwapDefaultRenderingPreference;

			state.lipSyncVideoAssetData = lipSyncDefaultVideoAssetData;
			state.lipSyncVoiceData = lipSyncDefaultVoiceData;
			state.lipSyncScript = lipSyncDefaultScript;
			state.lipSyncImageAssetData = lipSyncDefaultImageAssetData;
			state.lipSyncAudioAssetData = lipSyncDefaultAudioAssetData;

			state.selectedLoras = [];
			state.getCanvasFeedAPIStage = ApiStage.IDLE;

			state.upscaleInputImage = upscaleDefaultImage;
			state.upscaleOutputResolution = upscaleDefaultOutputResolution;
			state.upscaleCanvasImageAdded = upscaleDefaultCanvasImageAdded;

			state.videoAnyoneInputImage = videoAnyoneDefaultImage;
			state.faceReplaceInputImage = null;
			state.faceReplaceInputImageId = '';
			state.videoAnyoneCanvasImageAdded = videoAnyoneDefaultCanvasImageAdded;
			state.videoAnyonePrompt = videoAnyoneDefaultPrompt;
			state.videoAnyoneDuration = null;
			state.videoAnyoneInputImageId = '';
			state.mediaToolsSidebarView = null;
			state.canvasAppBarMediaType = StudioMediaTypeEnums.IMAGE;

			state.videoFaceSwapInputImage = videoFaceSwapDefaultImage;
			state.videoFaceSwapInputVideo = videoFaceSwapDefaultVideo;
			state.musicVideoAudioAssetData = null;
		},
		setCanvasSelectedModels: (state, action) => {
			const { models, model, replace = false } = action.payload;
			let selectedModelsCopy = [...state.selectedModels];

			// overwrite entire list of models on replace = true
			// otherwise add/remove single model passed as param
			if (replace) {
				selectedModelsCopy = models;
			} else if (selectedModelsCopy.includes(model)) {
				selectedModelsCopy = selectedModelsCopy.filter((modelName) => modelName !== model);
			} else {
				selectedModelsCopy.push(model);
			}

			state.selectedModels = selectedModelsCopy;
		},
		setCanvasSelectedLightningModels: (state, action) => {
			state.selectedLightningModels = toggleModelSelection(
				[...state.selectedLightningModels],
				action.payload,
			);
		},
		setCanvasSelectedFluxModels: (state, action) => {
			state.selectedFluxModels = toggleModelSelection(
				[...state.selectedFluxModels],
				action.payload,
			);
		},
		setCanvasSelectedStyles: (state, action) => {
			const { styles, style, replace = false } = action.payload;
			let selectedStylesCopy = [...state.selectedStyles];

			// overwrite entire list of models on replace = true
			// otherwise add/remove single model passed as param
			if (replace) {
				selectedStylesCopy = styles;
			} else if (selectedStylesCopy.includes(style)) {
				selectedStylesCopy = selectedStylesCopy.filter((styleId) => styleId !== style);
			} else {
				selectedStylesCopy.push(style);
			}

			state.selectedStyles = selectedStylesCopy;
		},
		setCanvasSelectedFluxStyles: (state, action) => {
			state.selectedFluxStyles = toggleModelSelection(
				[...state.selectedFluxStyles],
				action.payload,
			);
		},
		setCanvasPromptInput: (state, action) => {
			const { canvasId } = state;
			const inputPrompt = action.payload;
			state.promptInput = { ...state.promptInput, [canvasId]: inputPrompt };
		},
		setCanvasNegativePromptInput: (state, action) => {
			state.negativePromptInput = action.payload;
		},
		setCanvasSamplerInput: (state, action) => {
			state.samplerInput = action.payload;
		},
		setCanvasSamplingStepsInput: (state, action) => {
			state.samplingStepsInput = action.payload;
		},
		setCanvasAIGuidanceInput: (state, action) => {
			state.AIGuidanceInput = action.payload;
		},
		setCanvasBatchSizeInput: (state, action) => {
			state.batchSizeInput = action.payload;
		},
		setCanvasSeedInput: (state, action) => {
			state.seedInput = action.payload;
		},
		setCanvasRandomiseSeedInput: (state, action) => {
			state.randomiseSeedInput = action.payload;
		},
		setCanvasAspectRatioInput: (state, action) => {
			state.aspectRatioInput = action.payload;
		},

		setCanvasQuality: (state, action) => {
			state.quality = action.payload;
		},
		setCanvasPriority: (state, action) => {
			state.priority = action.payload;
		},

		toggleSaveGenerationalParametersInLocalStorage: (state) => {
			state.toggleSaveGenerationalParametersInLocalStorage =
				!state.toggleSaveGenerationalParametersInLocalStorage;
		},

		toggleTooManyCanvasGenerationsModal: (state, action) => {
			state.openTooManyGenerationsModal = action.payload;
		},
		toggleTooManyVideoGenerationsModal: (state, action) => {
			state.openTooManyVideoGenerationsModal = action.payload;
		},

		setCanvasFeedMap: (state, action) => {
			state.canvasFeedDataMap = action.payload;
		},
		setGetCanvasFeedAPIStage: (state, action) => {
			this.getCanvasFeedAPIStage = action.payload;
		},
		removeFailedMediaFromCanvasFeedMap: (state, action) => {
			const itemIds = action.payload;
			const { canvasId } = state;
			state.canvasFeedDataMap = removeCanvasFeedItems(
				state.canvasFeedDataMap,
				canvasId,
				'media',
				itemIds,
			);
		},
		setCanvasPendingUpdateLists: (state, action) => {
			const { mediaList, assetsList } = action.payload;
			state.canvasPendingUpdateMediaList = mediaList;
			state.canvasPendingUpdateAssetsList = assetsList;
		},
		setCanvasPendingUpdateMediaList: (state, action) => {
			state.canvasPendingUpdateMediaList = action.payload;
		},
		updateCanvasPendingUpdateMediaList: (state, action) => {
			state.canvasPendingUpdateMediaList = [
				...state.canvasPendingUpdateMediaList,
				...(action.payload ?? []),
			];
		},

		setCanvasPendingUpdateAssetsList: (state, action) => {
			state.canvasPendingUpdateAssetsList = action.payload;
		},
		updateCanvasPendingUpdateAssetsList: (state, action) => {
			state.canvasPendingUpdateAssetsList = [
				...state.canvasPendingUpdateAssetsList,
				...(action.payload ?? []),
			];
		},

		setCanvasControlNetEnabled: (state, action) => {
			state.controlNetEnabled = action.payload;
		},
		setCanvasControlNetNonSDXLType: (state, action) => {
			state.controlNetNonSDXLType = action.payload;
		},
		setCanvasControlNetSDXLType: (state, action) => {
			state.controlNetSDXLType = action.payload;
		},
		setCanvasControlNetFluxType: (state, action) => {
			state.controlNetFluxType = action.payload;
		},
		setCanvasControlNetImage: (state, action) => {
			state.controlNetImage = action.payload;
		},
		resetCanvasControlNetImage: (state) => {
			state.controlNetImage = null;
			state.controlNetEnabled = false;
		},
		setCanvasControlNetControlMode: (state, action) => {
			state.controlNetControlMode = action.payload;
		},
		setCanvasControlNetResizeMode: (state, action) => {
			state.controlNetResizeMode = action.payload;
		},

		// TrueTouch Image and settings
		setCanvasTrueTouchImage: (state, action) => {
			state.trueTouchImage = action.payload;
		},
		resetCanvasTrueTouchImage: (state) => {
			state.trueTouchImage = null;
		},
		setCanvasTrueTouchScaleFactor: (state, action) => {
			state.trueTouchScaleFactor = action.payload;
		},
		setCanvasTrueTouchRealismEffect: (state, action) => {
			state.trueTouchRealismEffect = action.payload;
		},
		setTrueTouchCanvasImageAdded: (state, action) => {
			state.trueTouchCanvasImageAdded = action.payload;
		},

		// 8k-Upscale Image and settings
		setCanvasUpscaleImage: (state, action) => {
			state.upscaleInputImage = action.payload;
		},
		resetCanvasUpscaleImage: (state) => {
			state.upscaleInputImage = null;
		},
		setCanvasUpscaleOutputResolution: (state, action) => {
			state.upscaleOutputResolution = action.payload;
		},
		setUpscaleCanvasImageAdded: (state, action) => {
			state.upscaleCanvasImageAdded = action.payload;
		},

		// video anyone image
		setCanvasVideoAnyoneImage: (state, action) => {
			state.videoAnyoneInputImage = action.payload;
		},
		setCanvasVideoAnyoneImageId: (state, action) => {
			state.videoAnyoneInputImageId = action.payload;
		},
		resetCanvasVideoAnyoneImage: (state) => {
			state.videoAnyoneInputImage = null;
			state.videoAnyoneInputImageId = '';
		},
		setVideoAnyoneCanvasImageAdded: (state, action) => {
			state.videoAnyoneCanvasImageAdded = action.payload;
		},
		setCanvasVideoAnyonePrompt: (state, action) => {
			state.videoAnyonePrompt = action.payload;
		},
		setCanvasVideoAnyoneDuration: (state, action) => {
			state.videoAnyoneDuration = action.payload;
		},

		// video face swap image
		setVideoFaceSwapImage: (state, action) => {
			state.videoFaceSwapInputImage = action.payload;
		},
		resetCanvasVideoFaceSwapImage: (state) => {
			state.videoFaceSwapInputImage = null;
		},

		// video face swap video
		setVideoFaceSwapVideo: (state, action) => {
			state.videoFaceSwapInputVideo = action.payload;
		},
		resetCanvasVideoFaceSwapVideo: (state) => {
			state.videoFaceSwapInputVideo = null;
		},

		// face replace image
		setCanvasFaceReplaceImage: (state, action) => {
			state.faceReplaceInputImage = action.payload;
		},
		setCanvasFaceReplaceImageId: (state, action) => {
			state.faceReplaceInputImageId = action.payload;
		},
		resetCanvasFaceReplaceImage: (state) => {
			state.faceReplaceInputImage = null;
			state.faceReplaceInputImageId = '';
		},

		setCanvasFaceReplaceTargetImages: (state, action) => {
			const { index } = action.payload;
			const targetImages = [...state.faceReplaceTargetImages];
			const existingIndex = targetImages.findIndex((img) => img.index === index);

			if (existingIndex !== -1) {
				targetImages[existingIndex] = action.payload;
			} else {
				targetImages.push(action.payload);
			}

			state.faceReplaceTargetImages = targetImages;
		},
		removeCanvasFaceReplaceTargetImage: (state, action) => {
			const index = action.payload;
			state.faceReplaceTargetImages = state.faceReplaceTargetImages.filter(
				(img) => img.index !== index,
			);
		},
		resetCanvasFaceReplaceTargetImages: (state) => {
			state.faceReplaceTargetImages = [];
		},

		setCanvasFaceReplaceTargetImageIndex: (state, action) => {
			state.faceReplaceTargetImageIndex = action.payload;
		},

		// Face swap image and settings
		setCanvasFaceSwapImageAssetData: (state, action) => {
			state.faceSwapImageAssetData = action.payload;
			state.faceSwapCanvasImageAdded = true;
		},
		resetCanvasFaceSwapImageAssetData: (state) => {
			state.faceSwapImageAssetData = null;
			state.faceSwapCanvasImageAdded = false;
		},
		setCanvasFaceSwapImageAdded: (state, action) => {
			state.faceSwapCanvasImageAdded = action.payload;
		},

		setCanvasFaceSwapVideoAdded: (state, action) => {
			state.faceSwapCanvasVideoAdded = action.payload;
		},
		// Face swap video and settings
		setCanvasFaceSwapVideoAssetData: (state, action) => {
			if (action.payload?.id) {
				state.faceSwapVideoAssetData = action.payload;
				state.faceSwapCanvasVideoAdded = true;
			}
		},
		resetCanvasFaceSwapVideoAssetData: (state) => {
			state.faceSwapVideoAssetData = null;
			state.faceSwapCanvasVideoAdded = false;
			state.faceSwapRenderingPreference = null;
		},
		setCanvasFaceSwapRenderingPreference: (state, action) => {
			state.faceSwapRenderingPreference = action.payload;
		},
		setFaceSwapFrames: (state, action) => {
			state.faceSwapFrames = action.payload;
		},

		setCanvasLipSyncVideoAssetData: (state, action) => {
			state.lipSyncVideoAssetData = action.payload;
		},
		resetCanvasLipSyncVideoAssetData: (state) => {
			state.lipSyncVideoAssetData = lipSyncDefaultVideoAssetData;
		},
		setCanvasLipSyncAudioAssetData: (state, action) => {
			state.lipSyncAudioAssetData = action.payload;
		},
		resetCanvasLipSyncAudioAssetData: (state) => {
			state.lipSyncAudioAssetData = lipSyncDefaultVideoAssetData;
		},

		setCanvasLipSyncVoiceData: (state, action) => {
			state.lipSyncVoiceData = action.payload;
		},
		setCanvasLipSyncScript: (state, action) => {
			state.lipSyncScript = action.payload;
		},
		setCanvasLipSyncImageAssetData: (state, action) => {
			state.lipSyncImageAssetData = action.payload;
		},
		resetCanvasLipSyncImageAssetData: (state) => {
			state.lipSyncImageAssetData = lipSyncDefaultImageAssetData;
		},
		setCanvasIsLipSyncFreeVideoUsed: (state, action) => {
			state.lipSyncIsFreeVideoUsed = action.payload;
		},

		setCanvasFacelockEnabled: (state, action) => {
			state.facelockEnabled = action.payload;
		},
		setCanvasFacelockImage: (state, action) => {
			state.facelockImage = action.payload;
		},
		resetCanvasFacelockImage: (state) => {
			state.facelockImage = null;
			state.facelockEnabled = false;
		},

		setMoveStageToLatestGeneration: (state, action) => {
			state.moveStageToLatestGeneration = action.payload;
		},

		setScaleCanvasStage: (state, action) => {
			state.scaleCanvasStage = action.payload;
		},
		setFitToScreen: (state, action) => {
			state.fitToScreen = action.payload;
		},
		resetCreatedCanvasStates(state) {
			state.createdCanvasId = null;
			state.createCanvasApiStage = ApiStage.IDLE;
		},
		setSelectedLoras: (state, action) => {
			state.selectedLoras = action.payload;
		},
		updateCanvasName(state, action) {
			const { id, name } = action.payload;
			const currentCanvas = state.canvases.find((can) => can.id === id);
			if (currentCanvas) {
				currentCanvas.name = name;
			}
		},
		modifyCanvasName(state, action) {
			const { name } = action.payload;
			const currentCanvas = state.canvasProperty;
			currentCanvas.name = name;
		},
		setCanvasScaled: (state, action) => {
			state.canvasScaled = action.payload;
		},
		setIsCanvasDraggable: (state, action) => {
			state.isCanvasDraggable = action.payload;
		},
		setCanvasViewportCenter: (state, action) => {
			if (state.canvasId != null) state.canvasViewportCenterMap[state.canvasId] = action.payload;
		},
		updateImageMaxMinPositionCoOrdinate(state, action) {
			const { x, y } = action.payload;
			if (!state.imagePositionWithLeastCoOrdinate) {
				state.imagePositionWithLeastCoOrdinate = { x, y };
			}
			if (!state.imagePositionWithMaxCoOrdinate) {
				state.imagePositionWithMaxCoOrdinate = { x, y };
			}
			if (x < state.imagePositionWithLeastCoOrdinate.x) {
				state.imagePositionWithLeastCoOrdinate.x = x;
			}
			if (y < state.imagePositionWithLeastCoOrdinate.y) {
				state.imagePositionWithLeastCoOrdinate.y = y;
			}
			if (x > state.imagePositionWithMaxCoOrdinate.x) {
				state.imagePositionWithMaxCoOrdinate.x = x;
			}
			if (y > state.imagePositionWithMaxCoOrdinate.y) {
				state.imagePositionWithMaxCoOrdinate.y = y;
			}
		},
		setCurrentProModeTab: (state, action) => {
			state.currentProModeTab = action.payload;
		},
		setIsImageSelected: (state, action) => {
			state.isImageSelected = action.payload;
		},
		setIsMultipleImagesSelected: (state, action) => {
			state.isMultipleImagesSelected = action.payload;
		},
		setIsImageRemixMode: (state, action) => {
			state.isImageRemixMode = action.payload;
		},
		setCurrentGenerationStyleTab: (state, action) => {
			state.currentGenerationStyleTab = action.payload;
		},
		toggleCanvasMediaFeedbackModal: (state, action) => {
			state.canvasMediaFeedbackModalOpen = action.payload;
		},
		// setCanvasAssetsMap: (state, action) => {
		// 	state.canvasAssetsMap = action.payload;
		// },
		setCanvasAssetsMap: (state, action) => {
			const { canvasId, canvasFeedDataMap } = state;
			state.canvasFeedDataMap = addCanvasFeedItems(
				canvasFeedDataMap,
				canvasId,
				'assets',
				action.payload,
			);
		},
		setSelectedCanvasMediaThumbnail: (state, action) => {
			state.selectedCanvasMediaThumbnail = {
				thumbnail: action.payload.thumbnail,
			};
		},
		resetSelectedCanvasMediaThumbnail: (state) => {
			state.selectedCanvasMediaThumbnail = null;
		},

		setSelectedCanvasMedia: (state, action) => {
			state.selectedCanvasMedia = action.payload;
		},
		resetSelectedCanvasMedia: (state) => {
			state.selectedCanvasMedia = null;
		},

		setCanvasAppBarMediaType: (state, action) => {
			state.canvasAppBarMediaType = action.payload;
		},
		setCanvasMediaToolsSidebarView: (state, action) => {
			state.mediaToolsSidebarView = action.payload;
		},
		setCanvasMusicVideoAudioAssetData: (state, action) => {
			state.musicVideoAudioAssetData = action.payload;
		},
		resetCanvasMusicVideoAudioAssetData: (state) => {
			state.musicVideoAudioAssetData = null;
		},
		setCanvasMusicVideoTheme: (state, action) => {
			state.musicVideoTheme = action.payload;
		},
		setCanvasMusicVideoCharacter: (state, action) => {
			state.musicVideoCharacter = action.payload;
		},
		setCanvasMusicVideoAspectRatio: (state, action) => {
			state.musicVideoAspectRatio = action.payload;
		},

		// change clothing
		setCanvasClothingInputImage: (state, action) => {
			state.clothingInputImage = action.payload;
		},
		resetCanvasClothingInputImage: (state) => {
			state.clothingInputImage = { id: null, url: null };
		},
		setCanvasClothingTargetImage: (state, action) => {
			state.clothingTargetImage = action.payload;
		},
		resetCanvasClothingTargetImage: (state) => {
			state.clothingTargetImage = { id: null, url: null };
		},
		setCanvasClothingTypeValue: (state, action) => {
			state.clothingTypeValue = action.payload;
		},
		resetCanvasState: (state) => {
			Object.keys(initialState).forEach((key) => {
				state[key] = initialState[key];
			});
		},

		// Product Photo
		setCanvasProductPhotoImage: (state, action) => {
			state.productPhotoImage = action.payload;
		},
		setCanvasProductPhotoWithoutBackgroundImage: (state, action) => {
			state.productPhotoWithoutBackgroundImage = action.payload;
		},
		resetCanvasProductPhotoImage: (state) => {
			state.productPhotoImage = { id: null, url: null };
			state.productPhotoWithoutBackgroundImage = { id: null, url: null };
		},
		setCanvasProductPhotoBackgroundImage: (state, action) => {
			state.productPhotoBackgroundImage = action.payload;
		},
		setCanvasProductPhotoObjectLocationImage: (state, action) => {
			state.productPhotoObjectLocationImage = action.payload;
		},
		setCanvasProductPhotoCompositeImage: (state, action) => {
			state.productPhotoCompositeImage = action.payload;
		},
		resetCanvasProductPhotoBackgroundImage: (state) => {
			state.productPhotoBackgroundImage = { id: null, url: null };
			state.productPhotoCompositeImage = { id: null, url: null };
			state.productPhotoObjectLocationImage = { id: null, url: null };
		},
		setCanvasProductPhotoMode: (state, action) => {
			state.productPhotoMode = action.payload;
		},
		setCanvasProductPhotoReflection: (state, action) => {
			state.productPhotoReflection = action.payload;
		},
		resetCanvasProductPhotoInternalAssets: (state) => {
			state.productPhotoObjectLocationImage = { id: null, url: null, status: 'idle' };
			state.productPhotoCompositeImage = { id: null, url: null, status: 'idle' };
		},
		setCanvasProductPhotoObjectTransformValues: (state, action) => {
			state.productPhotoObjectTransformValues = action.payload;
		},
		toggleCanvasProductPhotoImageToVideoGuide: (state, action) => {
			const {
				modalOpen = state.productPhotoImageToVideoGuideToggle.modalOpen,
				doNotShowValue = state.productPhotoImageToVideoGuideToggle.doNotShowValue,
			} = action.payload;

			state.productPhotoImageToVideoGuideToggle = {
				modalOpen,
				doNotShowValue,
			};
		},
		setCanvasModels: (state, action) => {
			state.models = action.payload;
		},
		setCanvasStyles: (state, action) => {
			state.styles = action.payload;
		},
		setCanvasFluxModels: (state, action) => {
			state.fluxModels = action.payload;
		},
		setCanvasFluxStyles: (state, action) => {
			state.fluxStyles = action.payload;
		},

		setCanvasImageReferenceStyle: (state, action) => {
			state.imageReferenceStyle = action.payload;
		},
		resetCanvasImageReferenceStyle: (state) => {
			state.imageReferenceStyle = { id: null, url: null };
		},
		setCanvasIntensityCountInput: (state, action) => {
			state.intensityCountInput = action.payload;
		},

		setCanvasVideoAnyoneEndFrameImage: (state, action) => {
			state.videoAnyoneEndFrameImage = action.payload;
		},
		resetCanvasVideoAnyoneEndFrameImage: (state) => {
			state.videoAnyoneEndFrameImage = { id: null, url: null };
		},
		setCanvasVideoAnyoneModelName: (state, action) => {
			state.videoAnyoneModelName = action.payload;
		},
		setCanvasVideoAnyoneModelProvider: (state, action) => {
			state.videoAnyoneModelProvider = action.payload;
		},
	},
	extraReducers: (builder) => {
		// get models
		builder.addCase(getModelsAsync.pending, (state) => {
			state.getModelsAPIStage = ApiStage.LOADING;
		});
		builder.addCase(getModelsAsync.fulfilled, (state, action) => {
			const { data, args } = action.payload;
			if (data.length < 1) state.getModelsAPIStage = ApiStage.ERROR;

			state.getModelsAPIStage = ApiStage.SUCCESS;
			if (args?.type === GenerationModelTypes.LIGHTNING) {
				state.lightningModels = data;
			} else if (args?.type === GenerationModelTypes.FLUX) {
				state.fluxModels = data;
			} else {
				state.models = data;
			}
		});
		builder.addCase(getModelsAsync.rejected, (state) => {
			state.getModelsAPIStage = ApiStage.ERROR;
			sendToast(ToastType.REFRESH_ON_API_FAILURE);
		});

		// get loras
		builder.addCase(getLorasAsync.pending, (state) => {
			state.getLorasAPIStage = ApiStage.LOADING;
		});
		builder.addCase(getLorasAsync.fulfilled, (state, action) => {
			const { data } = action.payload;
			if (data.length < 1) state.getLorasAPIStage = ApiStage.ERROR;

			state.getLorasAPIStage = ApiStage.SUCCESS;
			state.loras = data;
		});
		builder.addCase(getLorasAsync.rejected, (state) => {
			state.getLorasAPIStage = ApiStage.ERROR;
			sendToast(ToastType.REFRESH_ON_API_FAILURE);
		});

		// get canvas feed
		builder.addCase(getCanvasFeedAsync.pending, (state) => {
			state.getCanvasFeedAPIStage = ApiStage.LOADING;
			state.isCanvasInvalidError = false;
			state.isCanvasNotPublicError = false;
		});
		// builder.addCase(getCanvasFeedAsync.fulfilled, (state, action) => {
		// 	const { formattedAssets } = action.payload;
		// 	state.getCanvasFeedAPIStage = ApiStage.SUCCESS;
		// 	state.canvasFeedMap = Object.keys(formattedAssets).length === 0 ? {} : formattedAssets;
		//
		// 	const { maxX, maxY } = findMaxXY(formattedAssets);
		// 	const { minX, minY } = findMinXY(formattedAssets);
		// 	state.imagePositionWithLeastCoOrdinate = { x: minX, y: minY };
		// 	state.imagePositionWithMaxCoOrdinate = { x: maxX, y: maxY };
		// });

		builder.addCase(getCanvasFeedAsync.fulfilled, (state, action) => {
			const { formattedAssets } = action.payload;
			state.getCanvasFeedAPIStage = ApiStage.SUCCESS;
			const { canvasFeedDataMap, canvasId } = state;
			const canvasItemsToBeRemoved = [];
			Object.keys(canvasFeedDataMap[canvasId]?.media ?? {}).forEach((itemId) => {
				if (!formattedAssets[itemId]) {
					canvasItemsToBeRemoved.push(itemId);
				}
			});
			state.canvasPendingUpdateMediaList = [
				...state.canvasPendingUpdateMediaList,
				// Add the removed items to the pending list for later deletion
				...canvasItemsToBeRemoved.map((itemId) => {
					return {
						...(canvasFeedDataMap[canvasId]?.media?.[itemId] ?? {}),
						isRemoved: true,
					};
				}),
				...Object.values(formattedAssets ?? {}),
			];
			state.canvasFeedDataMap = addCanvasFeedItems(
				canvasFeedDataMap,
				canvasId,
				'media',
				formattedAssets,
			);
			// Note: Commented out for now since its usage is not found
			// const { maxX, maxY } = findMaxXY(formattedAssets);
			// const { minX, minY } = findMinXY(formattedAssets);
			// state.imagePositionWithLeastCoOrdinate = { x: minX, y: minY };
			// state.imagePositionWithMaxCoOrdinate = { x: maxX, y: maxY };
		});

		builder.addCase(getCanvasFeedAsync.rejected, (state, action) => {
			const { error } = action;
			if (error?.code === RendernetErrors.INVALID_CREDENTIALS) {
				state.getCanvasFeedAPIStage = ApiStage.IDLE;
			} else {
				state.getCanvasFeedAPIStage = ApiStage.ERROR;
			}
			if (error?.code === RendernetErrors.INVALID_CANVAS_ID) {
				sendToast(ToastType.CUSTOM, error?.message);
				state.isCanvasInvalidError = true;
			} else if (error?.code === RendernetErrors.CANVAS_NOT_PUBLIC) {
				state.isCanvasNotPublicError = true;
			} else {
				handleSomethingWentWrongErrorWithToast('API request failed', error);
			}
		});

		builder.addCase(getCanvasAssetsAsync.pending, (state) => {
			state.getCanvasAssetsApiStage = ApiStage.LOADING;
		});
		// builder.addCase(getCanvasAssetsAsync.fulfilled, (state, action) => {
		// 	const formattedAssetsMap = action.payload;
		// 	state.getCanvasAssetsApiStage = ApiStage.SUCCESS;
		// 	state.canvasAssetsMap =
		// 		Object.keys(formattedAssetsMap).length === 0 ? {} : formattedAssetsMap;
		// });
		builder.addCase(getCanvasAssetsAsync.fulfilled, (state, action) => {
			const formattedAssetsMap = action.payload;
			const { canvasFeedDataMap, canvasId } = state;
			state.getCanvasAssetsApiStage = ApiStage.SUCCESS;
			const canvasItemsToBeRemoved = [];
			Object.keys(canvasFeedDataMap[canvasId]?.assets ?? {}).forEach((itemId) => {
				if (!formattedAssetsMap[itemId]) {
					canvasItemsToBeRemoved.push(itemId);
				}
			});
			state.canvasPendingUpdateAssetsList = [
				...state.canvasPendingUpdateMediaList,
				// Add the removed items to the pending list for later deletion
				...canvasItemsToBeRemoved.map((itemId) => {
					return {
						...(canvasFeedDataMap[canvasId]?.assets?.[itemId] ?? {}),
						isRemoved: true,
					};
				}),
				...Object.values(formattedAssetsMap ?? {}),
			];
			state.canvasFeedDataMap = addCanvasFeedItems(
				canvasFeedDataMap,
				canvasId,
				'assets',
				formattedAssetsMap,
			);
		});

		builder.addCase(getCanvasAssetsAsync.rejected, (state, action) => {
			const { error } = action;
			if (error?.code === RendernetErrors.INVALID_CREDENTIALS) {
				state.getCanvasAssetsApiStage = ApiStage.IDLE;
			} else {
				state.getCanvasAssetsApiStage = ApiStage.ERROR;
			}
			handleSomethingWentWrongErrorWithToast('API request failed', error);
		});

		// generate studio image
		builder.addCase(runGenerateCanvasMediaAsync.pending, (state) => {
			if (state.seedInput === '') {
				state.seedInput = seedDefaultInput;
				state.randomiseSeedInput = true;
			}
			state.generateCanvasImageAPIStage = ApiStage.LOADING;
		});
		// builder.addCase(runGenerateCanvasMediaAsync.fulfilled, (state, action) => {
		// 	const response = action.payload;
		//
		// 	const { formattedDerivedMedia } = response;
		//
		// 	state.generateCanvasImageAPIStage = ApiStage.SUCCESS;
		//
		// 	let currentCanvasMapCopy = state.canvasFeedMap;
		// 	const currentCanvasMapIds = Object.keys(currentCanvasMapCopy);
		// 	const sampleImageIds = Object.keys({});
		//
		// 	if (areArraysEqual(currentCanvasMapIds, sampleImageIds)) {
		// 		currentCanvasMapCopy = {};
		// 	}
		// 	state.canvasFeedMap = Object.assign(formattedDerivedMedia, currentCanvasMapCopy);
		// });

		builder.addCase(runGenerateCanvasMediaAsync.fulfilled, (state, action) => {
			const response = action.payload;

			const { formattedDerivedMedia } = response;

			state.generateCanvasImageAPIStage = ApiStage.SUCCESS;

			const { canvasFeedDataMap, canvasId } = state;
			state.canvasFeedDataMap = updateCanvasFeedItems(
				canvasFeedDataMap,
				canvasId,
				'media',
				formattedDerivedMedia,
			);

			state.canvasPendingUpdateMediaList = [
				...state.canvasPendingUpdateMediaList,
				...Object.values(formattedDerivedMedia ?? {}),
			];
			// console.log('Spatial Position recovered: ', positionData);
			// state.canvasFeedDataMap = updateCanvasFeedItems(
			// 	canvasFeedDataMap,
			// 	canvasId,
			// 	'media',
			// 	formattedDerivedMedia,
			// );
			//
			// state.canvasPendingUpdateMediaList = [
			// 	...state.canvasPendingUpdateMediaList,
			// 	...Object.values(formattedDerivedMedia ?? {}),
			// ];
			const [mediaId] = Object.keys(formattedDerivedMedia);
			if (
				formattedDerivedMedia?.[mediaId].canvasSettings.generationType ===
					GenerationType.OBJECT_PLACEMENT &&
				!state.productPhotoImageToVideoGuideToggle.doNotShowValue
			) {
				state.productPhotoImageToVideoGuideToggle = {
					...state.productPhotoImageToVideoGuideToggle,
					modalOpen: true,
				};
			}
		});

		builder.addCase(runGenerateCanvasMediaAsync.rejected, (state, action) => {
			state.generateCanvasImageAPIStage = ApiStage.ERROR;

			const error = action.error.code;

			LoggerUtil.logError('[Generate Canvas Media]: Error', action?.error);
			LoggerUtil.logInfo('[Generate Canvas Media]: Parameters', action?.meta?.arg);
			if (error === RendernetErrors.NSFW_PROMPT) {
				captureAmplitudeEvent(AmplitudeEvents.NSFW_TOAST);
				capturePosthogEvent(PostHogEvents.NSFW_TOAST);
				if (action?.meta?.arg?.generateParams?.videoAnyoneOptions) {
					sendToast(ToastType.GEN_FAILED_DUE_TO_CONTENT_MODERATION);
				} else sendToast(ToastType.NSFW_CONTENT);
			} else if (error === RendernetErrors.TOO_MANY_PENDING_IMAGES) {
				state.openTooManyGenerationsModal = true;
			} else if (error === RendernetErrors.TOO_MANY_VIDEO_GENERATIONS) {
				state.openTooManyVideoGenerationsModal = true;
			} else if (error === RendernetErrors.TOO_MANY_LIPSYNC_GENERATIONS) {
				sendToast(ToastType.TOO_MANY_LIPSYNC_GENERATIONS);
			} else if (
				error !== RendernetErrors.INVALID_CREDENTIALS &&
				error !== RendernetErrors.BLOCKED_USER &&
				error !== RendernetErrors.INSUFFICIENT_CREDITS
			) {
				sendToast(ToastType.GENERATE_API_ERROR);
			}
		});

		builder.addCase(updateCanvasAssetsAsync.pending, (state) => {
			state.isCanvasUpdating = true;
		});

		builder.addCase(updateCanvasAssetsAsync.fulfilled, (state, action) => {
			state.canvasNewAssetIdMap = {
				...state.canvasNewAssetIdMap,
				...(action.payload.assetIdMap ?? {}),
			};
			const { canvasFeedDataMap, canvasId } = state;
			state.canvasFeedDataMap = updateCanvasFeedItems(
				canvasFeedDataMap,
				canvasId,
				'assets',
				action.payload.assetDataMap ?? {},
			);
			state.isCanvasUpdating = false;
		});

		builder.addCase(updateCanvasAssetsAsync.rejected, (state) => {
			state.isCanvasUpdating = false;
		});

		builder.addCase(updateMediaPropertiesAsync.pending, (state) => {
			state.isCanvasUpdating = true;
		});
		builder.addCase(updateMediaPropertiesAsync.fulfilled, (state, action) => {
			state.isCanvasUpdating = false;
			const { canvasId } = state;
			const { canvasFeedDataMap } = state;
			const mediaData = action.payload;
			const updatedItems = {};
			mediaData.forEach((media) => {
				const { media_id: mediaId, position, scale } = media;
				if (canvasFeedDataMap?.[canvasId]?.media?.[mediaId]) {
					updatedItems[mediaId] = {
						...canvasFeedDataMap[canvasId].media[mediaId],
						...scale,
						...position,
						newImage: false,
					};
				}
			});
			state.canvasFeedDataMap = updateCanvasFeedItems(
				canvasFeedDataMap,
				canvasId,
				'media',
				updatedItems,
			);
		});
		builder.addCase(updateMediaPropertiesAsync.rejected, (state) => {
			state.isCanvasUpdating = false;
		});

		builder.addCase(deleteAssetAsync.pending, (state) => {
			state.isCanvasUpdating = true;
		});
		builder.addCase(deleteAssetAsync.fulfilled, (state) => {
			state.isCanvasUpdating = false;
		});
		builder.addCase(deleteAssetAsync.rejected, (state) => {
			state.isCanvasUpdating = false;
		});

		// upload controlnet image file
		builder.addCase(uploadCanvasControlNetImageFileAsync.fulfilled, (state, action) => {
			state.controlNetImage = action.payload;
			state.controlNetEnabled = true;
		});
		builder.addCase(uploadCanvasControlNetImageFileAsync.rejected, () => {
			sendToast(ToastType.UPLOAD_IMAGE_ERROR);
		});
		// upload controlnet image through link
		builder.addCase(uploadCanvasControlNetImageLinkAsync.fulfilled, (state, action) => {
			state.controlNetImage = action.payload;
			state.controlNetEnabled = true;
		});
		builder.addCase(uploadCanvasControlNetImageLinkAsync.rejected, () => {
			sendToast(ToastType.UPLOAD_IMAGE_ERROR);
		});

		// upload trueTouch image file
		builder.addCase(uploadCanvasTrueTouchImageFileAsync.pending, (state) => {
			state.isTrueTouchImageUploading = true;
		});
		builder.addCase(uploadCanvasTrueTouchImageFileAsync.fulfilled, (state, action) => {
			state.trueTouchImage = action.payload;
			state.isTrueTouchImageUploading = false;
		});
		builder.addCase(uploadCanvasTrueTouchImageFileAsync.rejected, (state) => {
			sendToast(ToastType.UPLOAD_IMAGE_ERROR);
			state.isTrueTouchImageUploading = false;
		});
		// upload trueTouch image through link
		builder.addCase(uploadCanvasTrueTouchImageLinkAsync.pending, (state) => {
			state.isTrueTouchImageUploading = true;
		});
		builder.addCase(uploadCanvasTrueTouchImageLinkAsync.fulfilled, (state, action) => {
			state.trueTouchImage = action.payload;
			state.isTrueTouchImageUploading = false;
		});
		builder.addCase(uploadCanvasTrueTouchImageLinkAsync.rejected, (state) => {
			sendToast(ToastType.UPLOAD_IMAGE_ERROR);
			state.isTrueTouchImageUploading = false;
		});

		// upload face swap image through link
		builder.addCase(uploadCanvasFaceSwapImageLinkAsync.pending, (state) => {
			state.faceSwapImageUploading = true;
		});
		builder.addCase(uploadCanvasFaceSwapImageLinkAsync.fulfilled, (state, action) => {
			state.faceSwapImageAssetData = action.payload;
			state.faceSwapImageUploading = false;
		});
		builder.addCase(uploadCanvasFaceSwapImageLinkAsync.rejected, (state) => {
			sendToast(ToastType.UPLOAD_IMAGE_ERROR);
			state.faceSwapImageUploading = false;
		});
		// upload facelock image file
		builder.addCase(uploadCanvasFaceLockImageFileAsync.fulfilled, (state, action) => {
			state.facelockImage = action.payload;
			state.facelockEnabled = true;
		});
		builder.addCase(uploadCanvasFaceLockImageFileAsync.rejected, () => {
			sendToast(ToastType.UPLOAD_IMAGE_ERROR);
		});
		// upload facelock image through link
		builder.addCase(uploadCanvasFaceLockImageLinkAsync.fulfilled, (state, action) => {
			state.facelockImage = action.payload;
			state.facelockEnabled = true;
		});
		builder.addCase(uploadCanvasFaceLockImageLinkAsync.rejected, () => {
			sendToast(ToastType.UPLOAD_IMAGE_ERROR);
		});

		// randomise prompt
		builder.addCase(randomiseCanvasPromptAsync.pending, (state) => {
			state.randomisePromptAPIStage = ApiStage.LOADING;
		});
		builder.addCase(randomiseCanvasPromptAsync.fulfilled, (state, action) => {
			const prompt = action.payload;

			const { canvasId } = state;
			state.promptInput = {
				...state.promptInput,
				[canvasId]: prompt?.prompt ?? promptDefaultInput,
			};
			state.negativePromptInput = prompt?.negativePrompt ?? negativePromptDefaultInput;

			state.selectedModels = [prompt?.model] ?? aspectRatioDefaultInput;

			state.aspectRatioInput = prompt?.aspectRatioType ?? aspectRatioDefaultInput;
			state.samplingStepsInput = prompt?.steps ?? samplingStepsDefaultInput;
			state.samplerInput = prompt?.sampler ?? samplerDefaultInput;
			state.AIGuidanceInput = prompt?.guidance ?? APGuidanceDefaultInput;

			state.seedInput = '-1';

			state.randomisePromptAPIStage = ApiStage.SUCCESS;
		});
		builder.addCase(randomiseCanvasPromptAsync.rejected, (state, action) => {
			state.randomisePromptAPIStage = ApiStage.ERROR;
			const { error } = action;
			handleSomethingWentWrongErrorWithToast('API request failed', error);
		});
		// list canvases
		builder.addCase(listCanvases.pending, (state) => {
			state.listCanvasApiStage = ApiStage.LOADING;
		});

		builder.addCase(listCanvases.fulfilled, (state, action) => {
			const response = action.payload;
			state.listCanvasApiStage = ApiStage.SUCCESS;
			state.canvases = response;
			state.refetchListCanvas = false;
		});
		builder.addCase('api/executeQuery/fulfilled', (state, action) => {
			if (action.meta.arg.endpointName === 'listCanvasData') {
				state.refetchListCanvas = true;
			}
		});
		builder.addCase(listCanvases.rejected, (state, action) => {
			state.listCanvasApiStage = ApiStage.ERROR;
			const { error } = action;
			handleSomethingWentWrongErrorWithToast('API request failed', error);
		});

		// delete canvas
		builder.addCase(deleteCanvasAsync.pending, (state) => {
			state.deleteCanvasApiStage = ApiStage.LOADING;
		});
		builder.addCase(deleteCanvasAsync.fulfilled, (state, action) => {
			const { id } = action.payload;
			state.canvases = state.canvases.filter((canvas) => canvas.id !== id);
			deleteCanvasData(id);
			state.deleteCanvasApiStage = ApiStage.SUCCESS;
			state.refetchListCanvas = true;
		});
		builder.addCase(deleteCanvasAsync.rejected, (state, action) => {
			state.deleteCanvasApiStage = ApiStage.ERROR;
			const { error } = action;
			handleSomethingWentWrongErrorWithToast('API request failed', error);
		});

		// get canvas
		builder.addCase(getCanvasAsync.pending, (state) => {
			state.getCanvasAPIStage = ApiStage.LOADING;
			state.isCanvasInvalidError = false;
			state.isCanvasNotPublicError = false;
		});
		builder.addCase(getCanvasAsync.fulfilled, (state, action) => {
			const response = action.payload;
			state.getCanvasAPIStage = ApiStage.SUCCESS;
			state.canvasProperty = response;
			const refetchCanvasData = { ...state.refetchCanvasData };
			delete refetchCanvasData[response.id];
			state.refetchCanvasData = refetchCanvasData;
		});
		builder.addCase(getCanvasAsync.rejected, (state, action) => {
			const errorCode = action.error.code;
			const errorMessage = action.error.message;
			state.getCanvasAPIStage = ApiStage.ERROR;
			const { isPublicMode } = action.meta.arg;
			if (errorCode === RendernetErrors.INVALID_CANVAS_ID) {
				if (!isPublicMode) sendToast(ToastType.CUSTOM, errorMessage);
				state.isCanvasInvalidError = true;
				return;
			}
			if (errorCode === RendernetErrors.CANVAS_NOT_PUBLIC) {
				state.isCanvasNotPublicError = true;
				return;
			}
			handleSomethingWentWrongErrorWithToast('API request failed', action.error);
		});

		// create canvas
		builder.addCase(createCanvasAsync.pending, (state) => {
			state.createCanvasApiStage = ApiStage.LOADING;
		});

		builder.addCase(createCanvasAsync.fulfilled, (state, action) => {
			const createdCanvas = action.payload;
			state.createCanvasApiStage = ApiStage.SUCCESS;
			state.createdCanvasId = createdCanvas.id;
			state.canvases = [createdCanvas, ...state.canvases];
			state.refetchListCanvas = true;
		});

		builder.addCase(createCanvasAsync.rejected, (state, action) => {
			const errorCode = action.error.code;
			const errorMessage = action.error.message;
			state.createCanvasApiStage = ApiStage.ERROR;
			if (errorCode === RendernetErrors.CANVAS_LIMIT_REACHED) {
				sendToast(ToastType.CUSTOM, errorMessage);
				return;
			}
			const { error } = action;
			handleSomethingWentWrongErrorWithToast('API request failed', error);
		});
		// update canvas
		builder.addCase(updateCanvasAsync.pending, (state) => {
			state.updateCanvasAPIStage = ApiStage.LOADING;
		});
		builder.addCase(updateCanvasAsync.fulfilled, (state, action) => {
			const updatedCanvas = {
				...(action.payload ?? {}),
				viewportCenter: action.meta.arg.viewportCenter,
			};
			const currentCanvases = [...state.canvases];
			state.canvases = [
				updatedCanvas,
				...currentCanvases.filter((canvas) => canvas?.id !== updatedCanvas?.id),
			];
			state.updateCanvasAPIStage = ApiStage.SUCCESS;
			state.refetchCanvasData[updatedCanvas.id] = true;
			state.refetchListCanvas = true;
		});
		builder.addCase(updateCanvasAsync.rejected, (state, action) => {
			state.updateCanvasAPIStage = ApiStage.ERROR;
			const { error } = action;
			handleSomethingWentWrongErrorWithToast('API request failed', error);
		});

		// delete image
		builder.addCase(deleteMediaAsync.pending, (state) => {
			state.deleteImageApiStage = ApiStage.LOADING;
		});
		builder.addCase(deleteMediaAsync.fulfilled, (state) => {
			state.deleteImageApiStage = ApiStage.SUCCESS;
		});
		builder.addCase(deleteMediaAsync.rejected, (state) => {
			state.deleteImageApiStage = ApiStage.ERROR;
			sendToast(ToastType.CUSTOM, 'Something went wrong. Please try again.');
		});

		// get model categories
		builder.addCase(getModelCategoriesAsync.pending, (state) => {
			state.getModelCategoriesAPIStage = ApiStage.LOADING;
		});
		builder.addCase(getModelCategoriesAsync.fulfilled, (state, action) => {
			const { data } = action.payload;
			state.getModelCategoriesAPIStage = ApiStage.SUCCESS;

			if (data.length < 1) state.getModelCategoriesAPIStage = ApiStage.ERROR;
			const sortedCategories = [...data].sort((category1, category2) =>
				category1.name?.toLowerCase() === 'all'
					? 1
					: category2.name?.toLowerCase() === 'all'
						? -1
						: category2.name?.toLowerCase().localeCompare(category1.name?.toLowerCase()),
			);
			state.modelCategories = [...sortedCategories];
		});
		builder.addCase(getModelCategoriesAsync.rejected, (state, action) => {
			state.getModelCategoriesAPIStage = ApiStage.ERROR;
			const { error } = action;
			handleSomethingWentWrongErrorWithToast('API request failed', error);
		});

		// get lora categories
		builder.addCase(getLoraCategoriesAsync.pending, (state) => {
			state.getLoraCategoriesAPIStage = ApiStage.LOADING;
		});

		builder.addCase(getLoraCategoriesAsync.fulfilled, (state, action) => {
			const { data } = action.payload;
			state.getLoraCategoriesAPIStage = ApiStage.SUCCESS;

			if (data.length < 1) state.getLoraCategoriesAPIStage = ApiStage.ERROR;

			if (data.length !== 4) {
				state.loraCategories = [CanvasLoraLocalCategories.ALL, ...data];
				return;
			}

			state.loraCategories = [CanvasLoraLocalCategories.ALL, data[3], data[2], data[0], data[1]];
		});

		builder.addCase(getLoraCategoriesAsync.rejected, (state, action) => {
			state.getLoraCategoriesAPIStage = ApiStage.ERROR;
			const { error } = action;
			handleSomethingWentWrongErrorWithToast('API request failed', error);
		});

		// get style categories
		builder.addCase(getStylesCategoryAsync.pending, (state) => {
			state.getStyleCategoriesApiStage = ApiStage.LOADING;
		});

		builder.addCase(getStylesCategoryAsync.fulfilled, (state, action) => {
			const { data } = action.payload;
			state.getStyleCategoriesApiStage = ApiStage.SUCCESS;
			state.styleCategories = data;
		});

		builder.addCase(getStylesCategoryAsync.rejected, (state, action) => {
			state.getStyleCategoriesApiStage = ApiStage.ERROR;
			const { error } = action;
			handleSomethingWentWrongErrorWithToast('API request failed', error);
		});

		// get styles
		builder.addCase(getStylesAsync.pending, (state) => {
			state.getStylesApiStage = ApiStage.LOADING;
		});
		builder.addCase(getStylesAsync.fulfilled, (state, action) => {
			const { data, args } = action.payload;
			state.getStylesApiStage = ApiStage.SUCCESS;
			if (args?.type === GenerationStyleTypes.FLUX) {
				state.fluxStyles = data;
			} else {
				state.styles = data;
			}
		});
		builder.addCase(getStylesAsync.rejected, (state, action) => {
			state.getStylesApiStage = ApiStage.ERROR;
			const { error } = action;
			handleSomethingWentWrongErrorWithToast('API request failed', error);
		});

		// Get Lip Sync Voices list
		builder.addCase(getLipSyncVoicesListAsync.pending, (state) => {
			state.lipSyncVoicesAPIStage = ApiStage.LOADING;
		});
		builder.addCase(getLipSyncVoicesListAsync.fulfilled, (state, action) => {
			state.lipSyncVoicesAPIStage = ApiStage.SUCCESS;
			const { data } = action.payload;
			let voicesList = data;
			if (data && data?.length) {
				voicesList = [...voicesList].sort((voice1, voice2) =>
					(voice1.name ?? '').localeCompare(voice2.name ?? ''),
				);
			}
			state.lipSyncVoicesList = voicesList;
		});
		builder.addCase(getLipSyncVoicesListAsync.rejected, (state) => {
			state.lipSyncVoicesAPIStage = ApiStage.ERROR;
			sendToast(ToastType.LIP_SYNC_VOICES_LIST_FETCH_ERROR);
		});
		// get control-net models
		builder.addCase(getControlNetModelsAsync.pending, (state) => {
			state.canvasControlNetModelsApiStage = ApiStage.LOADING;
		});
		builder.addCase(getControlNetModelsAsync.fulfilled, (state, action) => {
			const { data } = action.payload;
			state.controlNetModelsCanvas = data;
			state.canvasControlNetModelsApiStage = ApiStage.SUCCESS;
		});
		builder.addCase(getControlNetModelsAsync.rejected, (state, action) => {
			state.canvasControlNetModelsApiStage = ApiStage.ERROR;
			const { error } = action;
			handleSomethingWentWrongErrorWithToast('API request failed', error);
		});

		// submit media feedback
		builder.addCase(submitCanvasMediaFeedbackAsync.pending, (state) => {
			state.submitCanvasMediaFeedbackApiStage = ApiStage.LOADING;
		});
		builder.addCase(submitCanvasMediaFeedbackAsync.fulfilled, (state) => {
			state.submitCanvasMediaFeedbackApiStage = ApiStage.SUCCESS;
		});
		builder.addCase(submitCanvasMediaFeedbackAsync.rejected, (state) => {
			state.submitCanvasMediaFeedbackApiStage = ApiStage.ERROR;
		});

		// remove image background
		builder.addCase(productPhotoCanvasRemoveImageBackgroundAsync.pending, (state) => {
			state.productPhotoWithoutBackgroundImageApiStage = ApiStage.LOADING;
		});
		builder.addCase(productPhotoCanvasRemoveImageBackgroundAsync.fulfilled, (state, action) => {
			const { data } = action.payload;
			state.productPhotoWithoutBackgroundImage = { id: null, url: data?.output_image };
			state.productPhotoWithoutBackgroundImageApiStage = ApiStage.SUCCESS;
		});
		builder.addCase(productPhotoCanvasRemoveImageBackgroundAsync.rejected, (state) => {
			state.productPhotoWithoutBackgroundImageApiStage = ApiStage.ERROR;
			sendToast(ToastType.CUSTOM, 'Failed to remove background');
		});

		// Face replace cost
		builder.addCase(getCanvasFaceReplaceCostAsync.pending, (state) => {
			state.faceReplaceCostAPIStage = ApiStage.LOADING;
		});
		builder.addCase(getCanvasFaceReplaceCostAsync.fulfilled, (state, action) => {
			const { data } = action.payload;
			state.faceReplaceCost = data;
			state.faceReplaceCostAPIStage = ApiStage.SUCCESS;
		});
		builder.addCase(getCanvasFaceReplaceCostAsync.rejected, (state, action) => {
			state.faceReplaceCostAPIStage = ApiStage.ERROR;
			const { error } = action;
			handleSomethingWentWrongErrorWithToast('API request failed', error);
		});

		// audio asset templates
		builder.addCase(getAudioAssetsTemplatesAsync.pending, (state) => {
			state.musicVideoTemplatesApiStage = ApiStage.LOADING;
		});
		builder.addCase(getAudioAssetsTemplatesAsync.fulfilled, (state, action) => {
			const { data } = action.payload;
			state.musicVideoTemplatesApiStage = ApiStage.SUCCESS;
			state.musicVideoTemplatesData = data.map(formatTemplateData);
		});
		builder.addCase(getAudioAssetsTemplatesAsync.rejected, (state, action) => {
			state.musicVideoTemplatesApiStage = ApiStage.ERROR;
			const { error } = action;
			handleSomethingWentWrongErrorWithToast('API request failed', error);
		});

		builder.addCase(getMusicVideoAsync.fulfilled, (state, action) => {
			const { route } = action.meta.arg;
			const { canvasId } = state;
			const { data } = action.payload;
			const { id: derivedMediaId, state: generationState } = data;
			if (route === STUDIO && canvasId) {
				const canvasFeedDataMapCopy = { ...state.canvasFeedDataMap };
				canvasFeedDataMapCopy[canvasId] = {
					...canvasFeedDataMapCopy[canvasId],
					media: {
						...canvasFeedDataMapCopy[canvasId].media,
						[derivedMediaId]: {
							...canvasFeedDataMapCopy[canvasId].media[derivedMediaId],
							canvasSettings: {
								...canvasFeedDataMapCopy[canvasId].media[derivedMediaId].canvasSettings,
								musicVideoStates: generationState,
							},
						},
					},
				};
				state.canvasFeedDataMap = { ...canvasFeedDataMapCopy };
			}
		});
	},
});

export const {
	setCanvasId,
	setCanvasProperty,

	setCanvasSettings,
	resetCanvasSettings,

	setCanvasSelectedModels,
	setCanvasSelectedLightningModels,
	setCanvasSelectedFluxModels,
	setCanvasSelectedStyles,

	setCanvasPromptInput,
	setCanvasNegativePromptInput,
	setCanvasSamplerInput,
	setCanvasSamplingStepsInput,
	setCanvasAIGuidanceInput,
	setCanvasBatchSizeInput,
	setCanvasSeedInput,
	setCanvasRandomiseSeedInput,
	setCanvasAspectRatioInput,

	setCanvasQuality,
	setCanvasPriority,
	toggleSaveGenerationalParametersInLocalStorage,

	toggleTooManyCanvasGenerationsModal,
	toggleTooManyVideoGenerationsModal,

	setCanvasFeedMap,
	setCanvasAssetsMap,
	removeFailedMediaFromCanvasFeedMap,
	setCanvasPendingUpdateLists,
	setCanvasPendingUpdateMediaList,
	updateCanvasPendingUpdateMediaList,
	setCanvasPendingUpdateAssetsList,
	updateCanvasPendingUpdateAssetsList,

	setCanvasControlNetEnabled,
	setCanvasControlNetImage,
	resetCanvasControlNetImage,
	setCanvasControlNetNonSDXLType,
	setCanvasControlNetSDXLType,
	setCanvasControlNetFluxType,
	setCanvasControlNetControlMode,
	setCanvasControlNetResizeMode,

	setCanvasTrueTouchImage,
	setCanvasTrueTouchRealismEffect,
	setCanvasTrueTouchScaleFactor,
	resetCanvasTrueTouchImage,
	setTrueTouchCanvasImageAdded,

	setCanvasUpscaleImage,
	resetCanvasUpscaleImage,
	setCanvasUpscaleOutputResolution,
	setUpscaleCanvasImageAdded,

	setCanvasVideoAnyoneImage,
	setCanvasVideoAnyoneImageId,
	resetCanvasVideoAnyoneImage,
	setVideoAnyoneCanvasImageAdded,
	setCanvasVideoAnyonePrompt,
	setCanvasVideoAnyoneDuration,

	setCanvasFaceSwapImageAssetData,
	resetCanvasFaceSwapImageAssetData,
	setCanvasFaceSwapImageAdded,

	setCanvasFaceSwapVideoAssetData,
	resetCanvasFaceSwapVideoAssetData,

	setCanvasFaceSwapRenderingPreference,

	setCanvasLipSyncVideoAssetData,
	resetCanvasLipSyncVideoAssetData,
	setCanvasLipSyncImageAssetData,
	resetCanvasLipSyncImageAssetData,
	setCanvasLipSyncAudioAssetData,
	resetCanvasLipSyncAudioAssetData,
	setCanvasLipSyncScript,
	setCanvasLipSyncVoiceData,
	setCanvasIsLipSyncFreeVideoUsed,

	setCanvasFacelockEnabled,
	setCanvasFacelockImage,
	resetCanvasFacelockImage,

	setMoveStageToLatestGeneration,

	setScaleCanvasStage,
	setFitToScreen,

	resetCreatedCanvasStates,
	setSelectedLoras,
	updateCanvasName,
	modifyCanvasName,
	setCanvasScaled,
	setIsCanvasDraggable,
	setCanvasViewportCenter,
	updateImageMaxMinPositionCoOrdinate,
	setCurrentProModeTab,
	setIsImageSelected,
	setIsMultipleImagesSelected,
	setSelectedCanvasMedia,
	resetSelectedCanvasMedia,
	setIsImageRemixMode,
	setCurrentGenerationStyleTab,
	toggleCanvasMediaFeedbackModal,
	setCanvasSelectedFluxStyles,

	setCanvasFaceReplaceImage,
	setCanvasFaceReplaceImageId,
	resetCanvasFaceReplaceImage,
	setCanvasFaceReplaceTargetImageIndex,
	setCanvasFaceReplaceTargetImages,
	resetCanvasFaceReplaceTargetImages,
	removeCanvasFaceReplaceTargetImage,

	setSelectedCanvasMediaThumbnail,
	resetSelectedCanvasMediaThumbnail,
	setCanvasAppBarMediaType,
	setCanvasMediaToolsSidebarView,
	toggleCanvasAudioCroppingModalOpen,
	setCanvasMusicVideoAudioAssetData,
	resetCanvasMusicVideoAudioAssetData,
	setCanvasMusicVideoTheme,
	setCanvasMusicVideoCharacter,
	setCanvasMusicVideoAspectRatio,
	setCanvasClothingInputImage,
	setCanvasClothingTargetImage,
	setCanvasClothingTypeValue,
	resetCanvasClothingInputImage,
	resetCanvasClothingTargetImage,
	resetCanvasState,
	setCanvasProductPhotoImage,
	setCanvasProductPhotoWithoutBackgroundImage,
	resetCanvasProductPhotoImage,
	setCanvasProductPhotoBackgroundImage,
	setCanvasProductPhotoObjectLocationImage,
	setCanvasProductPhotoCompositeImage,
	resetCanvasProductPhotoBackgroundImage,
	setCanvasProductPhotoMode,
	setCanvasProductPhotoReflection,
	resetCanvasProductPhotoInternalAssets,
	setCanvasProductPhotoObjectTransformValues,
	toggleCanvasProductPhotoImageToVideoGuide,
	setCanvasModels,
	setCanvasStyles,
	setCanvasFluxModels,
	setCanvasFluxStyles,

	setCanvasImageReferenceStyle,
	resetCanvasImageReferenceStyle,
	setCanvasIntensityCountInput,

	setCanvasVideoAnyoneEndFrameImage,
	resetCanvasVideoAnyoneEndFrameImage,
	setCanvasVideoAnyoneModelName,
	setCanvasVideoAnyoneModelProvider,
} = canvasSlice.actions;

export const canvasIdSelector = (state) => state.canvas.canvasId;
export const modelsSelector = (state) => state.canvas.models;
export const getModelsAPIStageSelector = (state) => state.canvas.getModelsAPIStage;
export const modelCategoriesSelector = (state) => state.canvas.modelCategories;
export const modelCategoriesAPIStageSelector = (state) => state.canvas.getModelCategoriesAPIStage;
export const canvasSelectedModelsSelector = (state) => state.canvas.selectedModels;
export const canvasSelectedStylesSelector = (state) => state.canvas.selectedStyles;
export const canvasLightningModelsSelector = (state) => state.canvas.lightningModels;
export const canvasSelectedLightningModelsSelector = (state) =>
	state.canvas.selectedLightningModels;
export const canvasFluxModelsSelector = (state) => state.canvas.fluxModels;
export const canvasSelectedFluxModelsSelector = (state) => state.canvas.selectedFluxModels;
export const canvasFluxStylesSelector = (state) => state.canvas.fluxStyles;
export const canvasSelectedFluxStylesSelector = (state) => state.canvas.selectedFluxStyles;

export const lorasSelector = (state) => state.canvas.loras;
export const getLorasApiSelector = (state) => state.canvas.getLorasAPIStage;
export const selectedLorasSelector = (state) => state.canvas.selectedLoras;
export const loraCategoriesSelector = (state) => state.canvas.loraCategories;
export const loraCategoriesAPIStageSelector = (state) => state.canvas.getLoraCategoriesAPIStage;

// export const canvasFeedMapSelector = (state) => state.canvas.canvasFeedMap;
export const canvasFeedAPIStageSelector = (state) => state.canvas.getCanvasFeedAPIStage;

export const canvasFeedDataMapSelector = (state) => state.canvas.canvasFeedDataMap;

export const canvasFeedMapSelector = (state) => {
	return state.canvas.canvasFeedDataMap[state.canvas.canvasId]?.media || {};
};

export const canvasFeedMapByCanvasIdSelector = createSelector(
	[canvasFeedDataMapSelector, (state, canvasId) => canvasId],
	(canvasFeedDataMap, canvasId) => {
		return canvasFeedDataMap[canvasId] || {};
	},
);

export const canvasFeedPendingVideoGenerationsSelector = (state) => {
	return Object.values(state.canvas.canvasFeedDataMap[state.canvas.canvasId]?.media ?? {}).filter(
		(media) =>
			media.assetType === AssetType.VIDEO &&
			(media.status === MediaGenerationStatus.PENDING ||
				media.status === MediaGenerationStatus.INITIATED),
	);
};

export const canvasAssetsMapSelector = (state) =>
	state.canvas.canvasFeedDataMap[state.canvas.canvasId]?.assets || {};

export const canvasFeedDataSelector = (state) =>
	state.canvas.canvasFeedDataMap[state.canvas.canvasId] || {
		media: {},
		assets: {},
	};

// export const canvasAssetsMapSelector = (state) => state.canvas.canvasAssetsMap;
export const canvasAssetsAPIStageSelector = (state) => state.canvas.getCanvasAssetsApiStage;
export const canvasNewAssetIdMapSelector = (state) => state.canvas.canvasNewAssetIdMap;

export const canvasPromptInputSelector = createSelector(
	[(state) => state.canvas.canvasId, (state) => state.canvas.promptInput],
	(canvasId, promptInput) => {
		return promptInput?.[canvasId] ?? promptDefaultInput;
	},
);
export const canvasNegativePromptInputSelector = (state) => state.canvas.negativePromptInput;
export const canvasSamplerInputSelector = (state) => state.canvas.samplerInput;
export const canvasSamplingStepsInputSelector = (state) => state.canvas.samplingStepsInput;
export const canvasAIGuidanceInputSelector = (state) => state.canvas.AIGuidanceInput;
export const canvasBatchSizeInputSelector = (state) => state.canvas.batchSizeInput;
export const canvasSeedInputSelector = (state) => state.canvas.seedInput;
export const canvasRandomiseSeedInputSelector = (state) => state.canvas.randomiseSeedInput;
export const canvasAspectRatioInputSelector = (state) => state.canvas.aspectRatioInput;

export const generateCanvasImageAPIStageSelector = (state) =>
	state.canvas.generateCanvasImageAPIStage;

export const toggleSaveGenerationalParametersInLocalStorageSelector = (state) =>
	state.canvas.toggleSaveGenerationalParametersInLocalStorage;

export const openTooManyCanvasGenerationsModalSelector = (state) =>
	state.canvas.openTooManyGenerationsModal;
export const openTooManyVideoGenerationsModalSelector = (state) =>
	state.canvas.openTooManyVideoGenerationsModal;

export const canvasControlNetModelsSelector = (state) => state.canvas.controlNetModelsCanvas;
export const canvasControlNetModelsApiStageSelector = (state) =>
	state.canvas.canvasControlNetModelsApiStage;
export const canvasControlNetEnabledSelector = (state) => state.canvas.controlNetEnabled;
export const canvasControlNetNonSDXLTypeSelector = (state) => state.canvas.controlNetNonSDXLType;
export const canvasControlNetSDXLTypeSelector = (state) => state.canvas.controlNetSDXLType;
export const canvasControlNetFluxTypeSelector = (state) => state.canvas.controlNetFluxType;

export const canvasControlNetImageSelector = (state) => state.canvas.controlNetImage;
export const canvasControlNetControlModeSelector = (state) => state.canvas.controlNetControlMode;
export const canvasControlNetResizeModeSelector = (state) => state.canvas.controlNetResizeMode;

// TrueTouch Selectors
export const canvasTrueTouchImageSelector = (state) => state.canvas.trueTouchImage;
export const canvasIsTrueTouchImageUploadingSelector = (state) =>
	state.canvas.isTrueTouchImageUploading;
export const canvasTrueTouchScaleFactorSelector = (state) => state.canvas.trueTouchScaleFactor;
export const canvasTrueTouchRealismEffectSelector = (state) => state.canvas.trueTouchRealismEffect;
export const trueTouchCanvasImageAddedSelector = (state) => state.canvas.trueTouchCanvasImageAdded;

// 8k Upscale Selectors
export const canvasUpscaleImageSelector = (state) => state.canvas.upscaleInputImage;
export const canvasUpscaleOutputResolutionSelector = (state) =>
	state.canvas.upscaleOutputResolution;
export const upscaleCanvasImageAddedSelector = (state) => state.canvas.upscaleCanvasImageAdded;

// video anyone selectors
export const canvasVideoAnyoneImageSelector = (state) => state.canvas.videoAnyoneInputImage;
export const videoAnyoneCanvasImageAddedSelector = (state) =>
	state.canvas.videoAnyoneCanvasImageAdded;
export const canvasVideoAnyonePromptSelector = (state) => state.canvas.videoAnyonePrompt;
export const canvasVideoAnyoneDurationSelector = (state) => state.canvas.videoAnyoneDuration;

// face replace selectors
export const canvasFaceReplaceImageSelector = (state) => state.canvas.faceReplaceInputImage;
export const canvasFaceReplaceTargetImagesSelector = (state) =>
	state.canvas.faceReplaceTargetImages;
export const canvasFaceReplaceTargetImageIndexSelector = (state) =>
	state.canvas.faceReplaceTargetImageIndex;
export const canvasFaceReplaceCostSelector = (state) => state.canvas.faceReplaceCost;
export const canvasFaceReplaceCostAPIStageSelector = (state) =>
	state.canvas.faceReplaceCostAPIStage;

// video face swap selectors
export const canvasVideoFaceSwapImageSelector = (state) => state.canvas.faceSwapImageAssetData;
export const canvasVideoFaceSwapVideoSelector = (state) => state.canvas.faceSwapVideoAssetData;
export const faceSwapImageAddedSelector = (state) => state.canvas.faceSwapCanvasImageAdded;
export const canvasVideoFaceSwapRenderingPrefSelector = (state) =>
	state.canvas.faceSwapRenderingPreference;

export const faceSwapImageAssetDataSelector = (state) => state.canvas.faceSwapImageAssetData;
export const faceSwapImageUploadingSelector = (state) => state.canvas.faceSwapImageUploading;

export const lipSyncVideoAssetDataSelector = (state) => state.canvas.lipSyncVideoAssetData;
export const lipSyncVoiceDataSelector = (state) => state.canvas.lipSyncVoiceData;
export const lipSyncScriptSelector = (state) => state.canvas.lipSyncScript;
export const lipSyncImageAssetDataSelector = (state) => state.canvas.lipSyncImageAssetData;
export const lipSyncVoicesListSelector = (state) => state.canvas.lipSyncVoicesList;
export const lipSyncVoicesApiStageSelector = (state) => state.canvas.lipSyncVoicesAPIStage;
export const lipSyncAudioAssetDataSelector = (state) => state.canvas.lipSyncAudioAssetData;
export const lipSyncIsFreeVideoUsedSelector = (state) => state.canvas.lipSyncIsFreeVideoUsed;

export const canvasFacelockEnabledSelector = (state) => state.canvas.facelockEnabled;
export const canvasFacelockImageSelector = (state) => state.canvas.facelockImage;

export const moveStageToLatestGenerationSelector = (state) =>
	state.canvas.moveStageToLatestGeneration;

export const scaleCanvasStageSelector = (state) => state.canvas.scaleCanvasStage;

export const fitToScreenSelector = (state) => state.canvas.fitToScreen;

export const randomiseCanvasPromptAPIStageSelector = (state) =>
	state.canvas.randomisePromptAPIStage;

export const canvasesSelector = (state) => state.canvas.canvases;
export const listCanvasApiStageSelector = (state) => state.canvas.listCanvasApiStage;

export const createdCanvasIdSelector = (state) => state.canvas.createdCanvasId;
export const createCanvasApiStageSelector = (state) => state.canvas.createCanvasApiStage;

export const updateCanvasAPIStageSelector = (state) => state.canvas.updateCanvasAPIStage;

export const canvasSelector = (state) => state.canvas.canvasProperty;
export const canvasQualitySelector = (state) => state.canvas.quality;
export const canvasPrioritySelector = (state) => state.canvas.priority;
export const canvasScaledSelector = (state) => state.canvas.canvasScaled;
export const canvasDraggableSelector = (state) => state.canvas.isCanvasDraggable;

export const canvasStyleCategoriesSelector = (state) => state.canvas.styleCategories;
export const canvasStyleCategoriesApiStageSelector = (state) =>
	state.canvas.getStyleCategoriesApiStage;
export const canvasStylesSelector = (state) => state.canvas.styles;
export const canvasStylesApiStageSelector = (state) => state.canvas.getStylesApiStage;

export const currentProModeTabSelector = (state) => state.canvas.currentProModeTab;
export const currentGenerationStyleTabSelector = (state) => state.canvas.currentGenerationStyleTab;

export const isImageSelectedSelector = (state) => state.canvas.isImageSelected;
export const isMultipleImagesSelectedSelector = (state) => state.canvas.isMultipleImagesSelected;

export const isImageRemixModeSelector = (state) => state.canvas.isImageRemixMode;
export const canvasMediaTypeSelector = (state) => state.canvas.canvasAppBarMediaType;
export const canvasMediaToolsSidebarViewSelector = (state) => state.canvas.mediaToolsSidebarView;

export const selectedCanvasMediaThumbnailSelector = (state) =>
	state.canvas.selectedCanvasMediaThumbnail;

export const canvasShareModeSelector = (state) => {
	if (state.canvas.canvases?.length && state.canvas.canvasId) {
		const canvas = state.canvas.canvases.find((c) => c.id === state.canvas.canvasId);
		return canvas?.isPublic ? CanvasShareMode.PUBLIC : CanvasShareMode.PRIVATE;
	}
	return null;
};

export const canvasCoverImageSelector = (state) => {
	if (state.canvas.canvases?.length && state.canvas.canvasId) {
		const canvas = state.canvas.canvases.find((c) => c.id === state.canvas.canvasId);
		return canvas?.imageUrl ?? '';
	}
	return '';
};

export const canvasControlNetSettingsSelector = (state) => ({
	inputImage: state.canvas.controlNetImage,
	nonsdxl: state.canvas.controlNetNonSDXLType,
	sdxl: state.canvas.controlNetSDXLType,
	flux: state.canvas.controlNetFluxType,
	controlMode: state.canvas.controlNetControlMode,
	resizeMode: state.canvas.controlNetResizeMode,
});

export const canvasFacelockSettingsSelector = (state) => ({
	inputImage: state.canvas.facelockImage,
});

export const canvasSettingsSelector = createSelector(
	[
		canvasIdSelector,
		canvasSelectedModelsSelector,
		canvasSelectedLightningModelsSelector,
		canvasSelectedModelsSelector,
		canvasSelectedStylesSelector,
		selectedLorasSelector,
		canvasPromptInputSelector,
		canvasNegativePromptInputSelector,
		canvasSamplerInputSelector,
		canvasSamplingStepsInputSelector,
		canvasAIGuidanceInputSelector,
		canvasSeedInputSelector,
		canvasRandomiseSeedInputSelector,
		canvasAspectRatioInputSelector,
		canvasBatchSizeInputSelector,
		canvasQualitySelector,
		canvasControlNetSettingsSelector,
		canvasFacelockSettingsSelector,
	],
	(
		canvasId,
		selectedModels,
		selectedLightningModels,
		selectedFluxModels,
		selectedStyles,
		selectedLoras,
		promptInput,
		negativePromptInput,
		samplerInput,
		samplingStepsInput,
		AIGuidanceInput,
		seedInput,
		randomiseSeedInput,
		aspectRatioInput,
		batchSizeInput,
		quality,
		controlNetSettings,
		facelockSettings,
	) => ({
		selectedModels,
		selectedLightningModels,
		selectedFluxModels,
		selectedStyles,
		selectedLoras,
		promptInput: promptInput?.[canvasId] ?? promptDefaultInput,
		negativePromptInput,
		samplerInput,
		samplingStepsInput,
		AIGuidanceInput,
		batchSizeInput,
		seedInput,
		randomiseSeedInput,
		aspectRatioInput,
		quality,
		controlNetOptions: controlNetSettings,
		facelockOptions: facelockSettings,
	}),
);

export const canvasSelectedMediaSelector = (state) => state.canvas.selectedCanvasMedia;

export const canvasMediaFeedbackModalSelector = (state) =>
	state.canvas.canvasMediaFeedbackModalOpen;

export const canvasMusicVideoAudioAssetDataSelector = (state) =>
	state.canvas.musicVideoAudioAssetData;
export const canvasMusicVideoThemeSelector = (state) => state.canvas.musicVideoTheme;
export const canvasMusicVideoCharacterSelector = (state) => state.canvas.musicVideoCharacter;
export const canvasMusicVideoAspectRatioSelector = (state) => state.canvas.musicVideoAspectRatio;
export const canvasMusicVideoTemplatesDataSelector = (state) =>
	state.canvas.musicVideoTemplatesData;
export const canvasMusicVideoTemplatesApiStageSelector = (state) =>
	state.canvas.musicVideoTemplatesApiStage;

export const canvasClothingInputImageSelector = (state) => state.canvas.clothingInputImage;
export const canvasClothingTargetImageSelector = (state) => state.canvas.clothingTargetImage;
export const canvasClothingTypeValueSelector = (state) => state.canvas.clothingTypeValue;

export const canvasPendingUpdateMediaListSelector = (state) =>
	state.canvas.canvasPendingUpdateMediaList;
export const canvasPendingUpdateAssetsListSelector = (state) =>
	state.canvas.canvasPendingUpdateAssetsList;
export const canvasViewportCenterMapSelector = (state) => state.canvas.canvasViewportCenterMap;
export const canvasViewportCenterSelector = createSelector(
	[canvasViewportCenterMapSelector, canvasIdSelector],
	(canvasViewportCenterMap, canvasId) => {
		return canvasViewportCenterMap[canvasId] || null;
	},
);
// Product Photo selectors
export const canvasProductPhotoImageSelector = (state) => state.canvas.productPhotoImage;
export const canvasProductPhotoWithoutBackgroundImageApiStageSelector = (state) =>
	state.canvas.productPhotoWithoutBackgroundImageApiStage;
export const canvasProductPhotoWithoutBackgroundImageSelector = (state) =>
	state.canvas.productPhotoWithoutBackgroundImage;
export const canvasProductPhotoBackgroundImageSelector = (state) =>
	state.canvas.productPhotoBackgroundImage;
export const canvasProductPhotoObjectLocationImageSelector = (state) =>
	state.canvas.productPhotoObjectLocationImage;
export const canvasProductPhotoCompositeImageSelector = (state) =>
	state.canvas.productPhotoCompositeImage;
export const canvasProductPhotoModeSelector = (state) => state.canvas.productPhotoMode;
export const canvasProductPhotoReflectionSelector = (state) => state.canvas.productPhotoReflection;
export const canvasProductPhotoImageToVideoGuideToggleSelector = (state) =>
	state.canvas.productPhotoImageToVideoGuideToggle;

export const canvasImageReferenceStyleSelector = (state) => state.canvas.imageReferenceStyle;
export const canvasIntensityCountInputSelector = (state) => state.canvas.intensityCountInput;

export const canvasVideoAnyoneEndFrameImageSelector = (state) =>
	state.canvas.videoAnyoneEndFrameImage;
export const canvasVideoAnyoneModelNameSelector = (state) => state.canvas.videoAnyoneModelName;

export const handleCanvasProductPhotoAssetFromPusher = (newAsset) => async (dispatch, getState) => {
	const productPhotoObjectLocationImage = canvasProductPhotoObjectLocationImageSelector(getState());
	const productPhotoCompositeImage = canvasProductPhotoCompositeImageSelector(getState());
	if (newAsset.status === 'active' && newAsset.id === productPhotoObjectLocationImage.id) {
		dispatch(
			setCanvasProductPhotoObjectLocationImage({
				...productPhotoObjectLocationImage,
				status: 'success',
			}),
		);
	}
	if (newAsset.status === 'active' && newAsset.id === productPhotoCompositeImage.id) {
		dispatch(
			setCanvasProductPhotoCompositeImage({
				...productPhotoCompositeImage,
				status: 'success',
			}),
		);
	}
};

export default canvasSlice.reducer;
